import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { AlarmClock, ArrowLeft, CalendarDays, ChevronLeft, ChevronRight, Crosshair, LandPlot, LogIn, LogOut, MapPin, MoreHorizontal, Pencil, Plus, Save, Search, Trash2, UserRound, UserX, Users, X } from 'lucide-react'

import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/common/searchable-select'
import { Empty } from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Checkbox } from '@/components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { getErrorMessage } from '@/lib/api'
import { locationsApi } from '@/services/locations'
import admsDevicesApi from '@/services/adms-devices'
import { orgUsersApi, type OrgUser } from '@/services/org-users'
import { orgTreeApi, type OrgTreeServiceInstance } from '@/services/org-tree'
import { workersApi, type WorkerPayScale, type WorkerSkill } from '@/services/workers'
import { jobsApi, resolveJobLocationLabel } from '@/services/jobs'
import { assignmentsApi } from '@/services/assignments'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { buildJobsListPath } from '@/lib/jobs-list-navigation'
import { resolveEventFamilyAssignmentsCreatePath } from '@/lib/event-family-assignments-route'
import { normalizeOrgUserGroupKey } from '@/lib/org-user-groups'
import { getUserDropdownKeywords, getUserDropdownLabel } from '@/lib/user-display'
import { useGoogleMapsLoader } from '@/hooks/use-google-maps-loader'
import { resolveReturnToPath, withReturnTo } from '@/lib/return-navigation'
import { buildMapSearchDetails, getGooglePlaceCity, getMapSearchAddress, getMapSearchCity } from '@/lib/map-location'
import { canUseMapsForPost } from '@/lib/map-usage'
import { canAccessWorkerHotline } from '@/lib/effective-capabilities'
import { useEffectivePostAccess } from '@/hooks/use-effective-post-access'
import type { Assignment, Job, JobLocation, JobLocationPayload, Worker } from '@/types'
import { JobTitleLink } from '@/components/jobs/job-title-link'
import { JobTeamAssignmentControls } from '@/components/jobs/job-team-assignment-controls'
import { getAssignmentStatusLabel, resolveAssignmentStatus } from '@/lib/assignment-status'
import { getJobStatusLabel } from '@/lib/job-status'
import { getJobCategoryColor, resolveJobCategoryKey } from '@/lib/job-list-display'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  GoogleMap,
  OverlayView,
  Polygon,
  Marker,
  StandaloneSearchBox,
} from '@react-google-maps/api'
import { JobLocationPulseMarker } from '@/components/locations/job-location-pulse-marker'
import { LocationsSitesDirectory } from '@/pages/locations/sites-directory'
import { useAuthStore } from '@/stores/auth-store'

function JobCategoryBadge({
  job,
  className,
}: {
  job?: Pick<Job, 'category' | 'category_name'> | null
  className?: string
}) {
  const categoryName = job?.category_name?.trim()
  if (!categoryName) {
    return null
  }

  const categoryColor = getJobCategoryColor(resolveJobCategoryKey(job as Job))
  return (
    <Badge variant="outline" className={cn('text-[10px] font-medium', categoryColor.badge, className)}>
      {categoryName}
    </Badge>
  )
}

const buildCoordinateSchema = (min: number, max: number, axisLabel: string) =>
  z
    .string()
    .trim()
    .optional()
    .refine((value) => value === undefined || value === '' || Number.isFinite(Number(value)), {
      message: 'Enter a valid number',
    })
    .refine((value) => {
      if (value === undefined || value === '') {
        return true
      }
      const numericValue = Number(value)
      return numericValue >= min && numericValue <= max
    }, {
      message: `${axisLabel} must be between ${min} and ${max}`,
    })

const latitudeSchema = buildCoordinateSchema(-90, 90, 'Latitude')
const longitudeSchema = buildCoordinateSchema(-180, 180, 'Longitude')

const locationSchema = z.object({
  client_user: z.string().trim().optional(),
  place: z.string().trim().min(1, 'Place is required'),
  latitude: latitudeSchema,
  longitude: longitudeSchema,
  attendance_device: z.string().trim().optional(),
})

type LocationFormData = z.infer<typeof locationSchema>

type AssignmentCreatePayload = Partial<Assignment> & {
  allow_schedule_clash?: boolean
}

const defaultValues: LocationFormData = {
  client_user: '',
  place: '',
  latitude: '',
  longitude: '',
  attendance_device: '',
}

const MAX_GEOFENCE_POINTS = 9

const toGeofencePoints = (
  coordinates: unknown
): [number, number][] => {
  if (!Array.isArray(coordinates)) {
    return []
  }

  return coordinates
    .map((point) => toPointTuple(point))
    .filter(
  (point): point is [number, number] => point !== null
)
}

const toPointTuple = (
  value: unknown
): [number, number] | null => {
  if (!Array.isArray(value) || value.length !== 2) {
    return null
  }
  const lat = Number(value[0])
  const lon = Number(value[1])
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
    return null
  }
  return [lat, lon]
}

const calculateCenter = (
  points: [number, number][]
): [number, number] | null => {
  if (points.length === 0) {
    return null
  }

  const [latSum, lonSum] = points.reduce(
    (acc, point) => [acc[0] + point[0], acc[1] + point[1]],
    [0, 0],
  )
  return [latSum / points.length, lonSum / points.length]
}

const toNumberOrNull = (value?: string) => {
  const trimmed = value?.trim()
  if (!trimmed) {
    return null
  }

  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) {
    return null
  }

  return Number(parsed.toFixed(6))
}

const getWorkerInitials = (name?: string | null) => {
  if (!name?.trim()) return '??'
  return name.trim().split(/\s+/).map((p) => p[0]).join('').toUpperCase().slice(0, 2)
}

const isWorkerOnline = (worker: Worker) => {
  const raw: unknown = worker.is_online ?? worker.isOnline
  if (typeof raw === 'boolean') {
    return raw
  }
  if (typeof raw === 'number') {
    return raw === 1
  }
  if (typeof raw === 'string') {
    const normalized = raw.trim().toLowerCase()
    if (['1', 'true', 'yes', 'online', 'active'].includes(normalized)) {
      return true
    }
    if (['0', 'false', 'no', 'offline', 'inactive'].includes(normalized)) {
      return false
    }
  }
  return Boolean(raw)
}

const isWorkerHotlineActive = (worker: Worker) => {
  const status = String(worker.hotline_current_status ?? '').trim().toLowerCase()
  if (worker.hotline_is_active) {
    return true
  }
  return status === 'waiting' || status === 'approval'
}

const toNullableCoordinate = (value: unknown) => {
  if (value === null || value === undefined) {
    return null
  }

  if (typeof value === 'string' && value.trim() === '') {
    return null
  }

  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return null
  }

  return Number(parsed.toFixed(6))
}

const parseCenterLatLng = (value: unknown) => {
  if (typeof value !== 'string') {
    return null
  }

  const [rawLat, rawLng] = value.split(',').map((part) => part.trim())
  if (!rawLat || !rawLng) {
    return null
  }

  const lat = toNullableCoordinate(rawLat)
  const lng = toNullableCoordinate(rawLng)
  if (lat === null || lng === null) {
    return null
  }

  return { lat, lng }
}

const getWorkerLastLocation = (worker: Worker) => {
  const lat = toNullableCoordinate(worker.last_latitude ?? worker.last_lat)
  const lng = toNullableCoordinate(worker.last_longitude ?? worker.last_lan)

  if (lat === null || lng === null) {
    return null
  }

  return {
    lat,
    lng,
  }
}

const getWorkerPermanentLocation = (worker: Worker) => {
  const lat = toNullableCoordinate(worker.permanent_latitude)
  const lng = toNullableCoordinate(worker.permanent_longitude)

  if (lat === null || lng === null) {
    return null
  }

  return {
    lat,
    lng,
  }
}

const getWorkerPreferredLocation = (worker: Worker) => {
  const isOnline = isWorkerOnline(worker)
  if (isOnline) {
    const liveLocation = getWorkerLastLocation(worker)
    if (liveLocation) {
      return liveLocation
    }

    return getWorkerPermanentLocation(worker)
  }

  const permanentLocation = getWorkerPermanentLocation(worker)
  if (permanentLocation) {
    return permanentLocation
  }

  return getWorkerLastLocation(worker)
}

const getWorkerPreferredLocationText = (worker: Worker) => {
  const isOnline = isWorkerOnline(worker)
  if (isOnline) {
    const liveLabel = String(worker.last_location_text ?? '').trim()
    if (liveLabel) {
      return liveLabel
    }
    const liveLocation = getWorkerLastLocation(worker)
    if (liveLocation) {
      return `${liveLocation.lat}, ${liveLocation.lng}`
    }

    const permanentLabel = String(worker.permanent_location_text ?? '').trim()
    if (permanentLabel) {
      return permanentLabel
    }

    const permanentLocation = getWorkerPermanentLocation(worker)
    return permanentLocation ? `${permanentLocation.lat}, ${permanentLocation.lng}` : null
  }

  const addressLabel = String(worker.address ?? '').trim()
  if (addressLabel) {
    return addressLabel
  }

  const permanentLabel = String(worker.permanent_location_text ?? '').trim()
  if (permanentLabel) {
    return permanentLabel
  }

  const permanentLocation = getWorkerPermanentLocation(worker)
  if (permanentLocation) {
    return `${permanentLocation.lat}, ${permanentLocation.lng}`
  }

  const liveLabel = String(worker.last_location_text ?? '').trim()
  if (liveLabel) {
    return liveLabel
  }

  const liveLocation = getWorkerLastLocation(worker)
  return liveLocation ? `${liveLocation.lat}, ${liveLocation.lng}` : null
}

const getWorkerIdentifier = (worker: Worker) => String(worker.worker_id ?? worker.id)

const WORKER_ICON_PATH = 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z'

const dedupeOrgUsersByIdentity = (users: OrgUser[]) => {
  const seen = new Set<string>()
  return users.filter((user) => {
    const key = String(user.user_id ?? user.id)
    if (seen.has(key)) {
      return false
    }
    seen.add(key)
    return true
  })
}

const getWorkerSkillLabels = (worker: Worker) => {
  const labels = worker.worker_skill_mappings
    ?.map((skill) => skill.skill_role_title)
    .filter((label): label is string => Boolean(label?.trim())) ?? []

  return Array.from(new Set(labels))
}

const skillMatchesJobCategory = (skill: WorkerSkill, job?: Job | null) => {
  const skillCategoryId = skill.job_category
  if (skillCategoryId == null || String(skillCategoryId).trim() === '') {
    return true
  }

  const jobCategoryId = job?.category
    ?? (job as { category_id?: string | number | null } | null | undefined)?.category_id
  if (jobCategoryId == null || String(jobCategoryId).trim() === '') {
    return true
  }

  return String(skillCategoryId) === String(jobCategoryId)
}

const buildWorkerOnlyAssignmentPayload = (job: Job, worker: Worker): AssignmentCreatePayload => {
  const workerId = getWorkerIdentifier(worker)
  const numericWorkerId = Number(workerId)

  return {
    job: job.id,
    worker: Number.isFinite(numericWorkerId) ? numericWorkerId : workerId,
    status: 'pending',
    pay_type: 'per_hour',
    pay: 0,
    night_pay: 0,
    holiday_pay: 0,
    peak_pay: 0,
    pay_flat: 0,
    estimated_duration: toOptionalNumber(job.scheduled_hours ?? job.estimated_hours) ?? 0,
  }
}

const getServiceInstanceLocation = (instance?: OrgTreeServiceInstance | null) => {
  const lat = toNullableCoordinate(instance?.latitude ?? instance?.lat)
  const lng = toNullableCoordinate(instance?.longitude ?? instance?.lng ?? instance?.lon)

  if (lat === null || lng === null) {
    return null
  }

  return {
    lat,
    lng,
  }
}

const getJobCoordinate = (job?: Job | null) => {
  const lat = toNullableCoordinate(job?.latitude ?? job?.location_info?.latitude)
  const lng = toNullableCoordinate(job?.longitude ?? job?.location_info?.longitude)

  if (lat !== null && lng !== null) {
    return { lat, lng }
  }

  return parseCenterLatLng(job?.location_info?.center_latlog)
}

const toRadians = (value: number) => (value * Math.PI) / 180

const KM_TO_MILES = 0.621371
const JOB_LOCATION_VIEW_RADIUS_OPTIONS_KM = [10, 15, 25, 50] as const
const DEFAULT_JOB_LOCATION_VIEW_RADIUS_KM = 50
const HOUR_IN_MS = 60 * 60 * 1000
const KM_PER_LATITUDE_DEGREE = 111.32
const IMPERIAL_DISTANCE_COUNTRY_CODES = new Set(['US', 'LR', 'MM'])
type DistanceUnitSystem = 'metric' | 'imperial'

const getBoundingPointsForRadiusKm = (
  center: { lat: number; lng: number },
  radiusKm: number,
): Array<{ lat: number; lng: number }> => {
  if (radiusKm <= 0) {
    return []
  }

  const latDelta = radiusKm / KM_PER_LATITUDE_DEGREE
  const lngDelta = radiusKm / (KM_PER_LATITUDE_DEGREE * Math.cos(toRadians(center.lat)))

  return [
    { lat: center.lat + latDelta, lng: center.lng },
    { lat: center.lat - latDelta, lng: center.lng },
    { lat: center.lat, lng: center.lng + lngDelta },
    { lat: center.lat, lng: center.lng - lngDelta },
  ]
}

const resolveDistanceUnitSystem = (
  countryDistanceUnit?: string | null,
  countryCode?: string | null,
  countryName?: string | null,
): DistanceUnitSystem => {
  const normalizedCountryDistanceUnit = String(countryDistanceUnit ?? '').trim().toLowerCase()
  if (normalizedCountryDistanceUnit === 'imperial') {
    return 'imperial'
  }
  if (normalizedCountryDistanceUnit === 'metric') {
    return 'metric'
  }

  const normalizedCountryCode = String(countryCode ?? '').trim().toUpperCase()
  if (normalizedCountryCode && IMPERIAL_DISTANCE_COUNTRY_CODES.has(normalizedCountryCode)) {
    return 'imperial'
  }

  const normalizedCountryName = String(countryName ?? '').trim().toLowerCase()
  if (normalizedCountryName === 'united states' || normalizedCountryName === 'liberia' || normalizedCountryName === 'myanmar') {
    return 'imperial'
  }

  return 'metric'
}

const calculateDistanceKm = (
  from: { lat: number; lng: number } | null,
  to: { lat: number; lng: number } | null,
) => {
  if (!from || !to) {
    return null
  }

  const earthRadiusKm = 6371
  const latDelta = toRadians(to.lat - from.lat)
  const lngDelta = toRadians(to.lng - from.lng)
  const a = Math.sin(latDelta / 2) ** 2
    + Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(lngDelta / 2) ** 2
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return earthRadiusKm * c
}

const formatDistance = (distanceKm: number | null, unitSystem: DistanceUnitSystem) => {
  if (distanceKm === null) {
    return null
  }

  if (unitSystem === 'imperial') {
    const distanceMiles = distanceKm * KM_TO_MILES
    if (distanceMiles < 0.1) {
      return `${Math.round(distanceMiles * 5280)} ft`
    }

    return `${distanceMiles.toFixed(distanceMiles < 10 ? 1 : 0)} mi`
  }

  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)} m`
  }

  return `${distanceKm.toFixed(distanceKm < 10 ? 1 : 0)} km`
}

const formatJobLocationViewRadiusOption = (radiusKm: number, unitSystem: DistanceUnitSystem) => {
  if (unitSystem === 'imperial') {
    const distanceMiles = radiusKm * KM_TO_MILES
    return `${distanceMiles < 10 ? distanceMiles.toFixed(1) : Math.round(distanceMiles)} mi`
  }

  return `${radiusKm} km`
}

const parseJobDateTime = (dateTime?: string | null, time?: string | null) => {
  const base = String(dateTime ?? '').trim()
  if (!base) {
    return null
  }

  const value = time && !base.includes('T') && !/\d{1,2}:\d{2}/.test(base)
    ? `${base}T${time}`
    : base
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const formatJobDateTime = (value: Date) => new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
}).format(value)

const formatSelectedJobSchedule = (job?: Job | null) => {
  if (!job) {
    return null
  }

  const start = parseJobDateTime(job.scheduled_start ?? job.start_date, job.start_time)
  const end = parseJobDateTime(job.scheduled_end ?? job.end_date, job.end_time)

  if (!start && !end) {
    return 'Schedule not set'
  }

  if (start && end) {
    return `${formatJobDateTime(start)} - ${formatJobDateTime(end)}`
  }

  if (start) {
    return `Starts ${formatJobDateTime(start)}`
  }

  return `Ends ${formatJobDateTime(end!)}`
}

const formatJobDisplayId = (job?: Job | null, fallbackJobId?: string) => {
  const ref = String(job?.job_ref ?? '').trim()
  if (ref) {
    return ref
  }

  const id = String(job?.id ?? fallbackJobId ?? '').trim()
  if (id) {
    return `Job #${id}`
  }

  return 'Job location'
}

const formatPriorityLabel = (priority?: Job['priority'] | null) => {
  const value = String(priority ?? '').trim().toLowerCase()
  if (!value) {
    return 'Normal'
  }

  return value.charAt(0).toUpperCase() + value.slice(1)
}

const getPriorityBadgeClassName = (priority?: Job['priority'] | null) => {
  const value = String(priority ?? '').trim().toLowerCase()
  if (value === 'critical') {
    return 'border-rose-300 bg-rose-50 text-rose-700'
  }
  if (value === 'urgent') {
    return 'border-orange-300 bg-orange-50 text-orange-700'
  }
  if (value === 'high') {
    return 'border-amber-300 bg-amber-50 text-amber-700'
  }
  if (value === 'low') {
    return 'border-emerald-300 bg-emerald-50 text-emerald-700'
  }
  return 'border-slate-300 bg-slate-50 text-slate-700'
}

const getLiveAttendanceCounts = (job: Job) => {
  const source = job as unknown as Record<string, unknown>
  const readCount = (...keys: string[]) => {
    for (const key of keys) {
      const raw = source[key]
      if (raw === null || raw === undefined || raw === '') {
        continue
      }
      const parsed = Number(raw)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
    return undefined
  }

  const total = readCount('workers_count', 'total_workers', 'assigned_workers_count') ?? 0
  const checkedIn = readCount('checked_in_count', 'check_in_count', 'active_workers_count') ?? 0
  const checkedOut = readCount('checked_out_count', 'check_out_count', 'completed_workers_count') ?? 0
  const late = readCount('late_count', 'late_workers_count', 'delayed_workers_count', 'late_check_in_count') ?? 0
  const notShown = readCount('not_shown_count', 'not_show_count', 'no_show_count', 'pending_workers_count')
    ?? Math.max(total - checkedIn - checkedOut, 0)

  return {
    checkedIn: Math.max(checkedIn, 0),
    checkedOut: Math.max(checkedOut, 0),
    late: Math.max(late, 0),
    notShown: Math.max(notShown, 0),
  }
}

const getLivePrimaryWorkerLabel = (job: Job, overrideAssignedCount?: number) => {
  const counts = getLiveAttendanceCounts(job)
  const attendanceDerivedCount = Math.max(counts.checkedIn + counts.late + counts.checkedOut + counts.notShown, 0)
  const summaryCountRaw = Number(job.job_summary?.total_workers ?? 0)
  const summaryCount = Number.isFinite(summaryCountRaw) ? Math.max(summaryCountRaw, 0) : 0
  const directAssignedRaw = Number((job as Job & { assigned_workers_count?: number }).assigned_workers_count ?? 0)
  const directAssignedCount = Number.isFinite(directAssignedRaw) ? Math.max(directAssignedRaw, 0) : 0
  const overrideCount = Number.isFinite(Number(overrideAssignedCount)) ? Math.max(Number(overrideAssignedCount), 0) : 0
  const assignedCount = Math.max(overrideCount, attendanceDerivedCount, summaryCount, directAssignedCount)

  return `Workers ${assignedCount}`
}

type LiveWorkerStatus = 'Onsite' | 'Late' | 'Departed' | 'No show'

const getLiveWorkerStatus = (assignment: Assignment, worker?: Worker | null): LiveWorkerStatus => {
  const workflowText = `${assignment.workflow_status_label ?? ''} ${assignment.workflow_status_code ?? ''}`.trim().toLowerCase()
  const assignmentStatus = resolveAssignmentStatus(assignment.status)

  if (
    workflowText.includes('depart')
    || workflowText.includes('check out')
    || workflowText.includes('checked out')
    || workflowText.includes('complete')
    || assignmentStatus === 'completed'
  ) {
    return 'Departed'
  }

  if (workflowText.includes('late') || workflowText.includes('delay')) {
    return 'Late'
  }

  if (
    (worker ? isWorkerOnline(worker) : false)
    || workflowText.includes('onsite')
    || workflowText.includes('on site')
    || workflowText.includes('check in')
    || workflowText.includes('checked in')
    || workflowText.includes('arrived')
    || workflowText.includes('active')
  ) {
    return 'Onsite'
  }

  return 'No show'
}

const toDateKey = (value: Date) => {
  const year = value.getFullYear()
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const parseDateKey = (value: string | null) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null
  }

  const parsed = new Date(`${value}T00:00:00`)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const getJobScheduleDateKey = (job: Job) => {
  const candidate = String(job.scheduled_start ?? job.start_date ?? '').trim()
  if (!candidate) {
    return ''
  }

  return candidate.includes('T') ? candidate.slice(0, 10) : candidate.slice(0, 10)
}

const toOptionalNumber = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const isFlatPayType = (payType?: string | null) => (
  payType === 'per_job' || payType === 'per_week' || payType === 'per_month'
)

const formatPayScaleLabel = (scale: WorkerPayScale) => {
  const payType = String(scale.pay_type_label ?? scale.pay_type ?? '').replace(/_/g, ' ')
  const basePay = scale.base_pay !== undefined && scale.base_pay !== null && scale.base_pay !== ''
    ? `Base ${scale.base_pay}`
    : ''
  return [payType, basePay, scale.notes].filter(Boolean).join(' - ') || `Pay scale ${scale.id}`
}

const buildAssignmentPayload = ({
  job,
  skill,
  payScale,
}: {
  job: Job
  skill: WorkerSkill
  payScale?: WorkerPayScale | null
}) => {
  const paySource = payScale ?? skill
  const payType = (paySource.pay_type ?? 'per_hour') as NonNullable<Assignment['pay_type']>
  const basePay = payScale?.base_pay
    ?? skill.base_pay
    ?? skill.per_hour_pay_override
    ?? skill.day_pay_override
    ?? 0

  return {
    job: job.id,
    worker_skill: skill.id,
    status: 'pending' as const,
    pay_type: payType,
    pay: toOptionalNumber(basePay) ?? 0,
    night_pay: toOptionalNumber(payScale?.night_pay ?? skill.night_pay ?? skill.night_pay_override) ?? 0,
    holiday_pay: toOptionalNumber(payScale?.holiday_pay ?? skill.holiday_pay ?? skill.holiday_pay_override) ?? 0,
    peak_pay: toOptionalNumber(payScale?.peak_pay ?? skill.peak_pay) ?? 0,
    pay_flat: isFlatPayType(payType) ? (toOptionalNumber(basePay) ?? 0) : 0,
    estimated_duration: toOptionalNumber(job.scheduled_hours ?? job.estimated_hours),
  }
}

const firstMessageFromValue = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim()
  }

  if (Array.isArray(value)) {
    for (const entry of value) {
      const message = firstMessageFromValue(entry)
      if (message) {
        return message
      }
    }
  }

  return null
}

const extractAssignmentClashMessage = (error: unknown) => {
  const responseData = (error as { response?: { data?: unknown } })?.response?.data
  if (!responseData || typeof responseData !== 'object') {
    return null
  }

  const data = responseData as Record<string, unknown>
  const moreInfo = data.more_info && typeof data.more_info === 'object'
    ? data.more_info as Record<string, unknown>
    : null
  const message = firstMessageFromValue(data.worker_skill)
    ?? firstMessageFromValue(moreInfo?.worker_skill)
    ?? firstMessageFromValue(data.message)

  if (!message || !/already assigned|selected time slot/i.test(message)) {
    return null
  }

  return message
}

const buildServiceWorkerMarkerSvg = (color: string) => `
  <svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
    <path d="M18 1.5C10.2 1.5 4 7.6 4 15.1c0 9 10.3 17.1 13.1 19.1.5.4 1.3.4 1.8 0C21.7 32.2 32 24.1 32 15.1 32 7.6 25.8 1.5 18 1.5Z" fill="${color}" stroke="#fff" stroke-width="2"/>
    <circle cx="18" cy="15" r="10.5" fill="#0f172a" opacity=".18"/>
    <circle cx="18" cy="11.5" r="4.2" fill="#fff" stroke="#0f172a" stroke-width="1.2"/>
    <path d="M9.5 27.8c.7-5.1 3.6-8.1 8.5-8.1s7.8 3 8.5 8.1c-2.3 1.5-5.1 2.3-8.5 2.3s-6.2-.8-8.5-2.3Z" fill="#fff" stroke="#0f172a" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="m13.5 21.2 4.5 3.5 4.5-3.5" fill="none" stroke="${color}" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>
`.trim()

const getServiceWorkerMarkerUrl = (color: string) => (
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(buildServiceWorkerMarkerSvg(color))}`
)

// Static Marker-icon rendition of the original JobLocationPulseMarker component's look
// (rose pulse rings + a white map-pin glyph) — used because that component itself relies
// on an OverlayView, which doesn't reliably paint on the Google Maps branch (see the job
// location marker effect below).
const JOB_LOCATION_MARKER_SIZE = 56
const buildJobLocationPulseMarkerSvg = () => `
  <svg xmlns="http://www.w3.org/2000/svg" width="${JOB_LOCATION_MARKER_SIZE}" height="${JOB_LOCATION_MARKER_SIZE}" viewBox="0 0 56 56">
    <circle cx="28" cy="28" r="27" fill="#f43f5e" fill-opacity="0.25"/>
    <circle cx="28" cy="28" r="23" fill="#f43f5e" fill-opacity="0.35"/>
    <circle cx="28" cy="28" r="16" fill="#f43f5e" stroke="#fff" stroke-width="3"/>
    <g transform="translate(19,19) scale(0.75)" fill="#fff">
      <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/>
      <circle cx="12" cy="10" r="3" fill="#f43f5e"/>
    </g>
  </svg>
`.trim()

const getJobLocationPulseMarkerUrl = () => (
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(buildJobLocationPulseMarkerSvg())}`
)

export function ServiceWorkerMarkerGlyph({ color }: { color: string }) {
  return (
    <svg aria-hidden="true" className="h-9 w-9" viewBox="0 0 36 44">
      <path d="M18 1.5C10.2 1.5 4 7.6 4 15.1c0 9 10.3 17.1 13.1 19.1.5.4 1.3.4 1.8 0C21.7 32.2 32 24.1 32 15.1 32 7.6 25.8 1.5 18 1.5Z" fill={color} stroke="#fff" strokeWidth="2" />
      <circle cx="18" cy="15" r="10.5" fill="#0f172a" opacity="0.18" />
      <circle cx="18" cy="11.5" r="4.2" fill="#fff" stroke="#0f172a" strokeWidth="1.2" />
      <path d="M9.5 27.8c.7-5.1 3.6-8.1 8.5-8.1s7.8 3 8.5 8.1c-2.3 1.5-5.1 2.3-8.5 2.3s-6.2-.8-8.5-2.3Z" fill="#fff" stroke="#0f172a" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="m13.5 21.2 4.5 3.5 4.5-3.5" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

type LocationWorkerMarker = {
  worker: Worker
  position: {
    lat: number
    lng: number
  }
  kind: 'live' | 'permanent'
}

type LocationWorkerMarkerGroup = {
  workers: Worker[]
  position: {
    lat: number
    lng: number
  }
  kind: 'live' | 'permanent'
}

type WorkerMapBadge = {
  number: number
  colorHex: string
  bgClass: string
  darkBgClass: string
  tailBorderClass: string
}

const WORKER_MAP_BADGE_PALETTE: Array<Omit<WorkerMapBadge, 'number'>> = [
  { colorHex: '#ef4444', bgClass: 'bg-red-500', darkBgClass: 'bg-red-700', tailBorderClass: 'border-t-red-500' },
  { colorHex: '#0ea5e9', bgClass: 'bg-sky-500', darkBgClass: 'bg-sky-700', tailBorderClass: 'border-t-sky-500' },
  { colorHex: '#22c55e', bgClass: 'bg-green-500', darkBgClass: 'bg-green-700', tailBorderClass: 'border-t-green-500' },
  { colorHex: '#f59e0b', bgClass: 'bg-amber-500', darkBgClass: 'bg-amber-700', tailBorderClass: 'border-t-amber-500' },
  { colorHex: '#8b5cf6', bgClass: 'bg-violet-500', darkBgClass: 'bg-violet-700', tailBorderClass: 'border-t-violet-500' },
  { colorHex: '#ec4899', bgClass: 'bg-pink-500', darkBgClass: 'bg-pink-700', tailBorderClass: 'border-t-pink-500' },
  { colorHex: '#14b8a6', bgClass: 'bg-teal-500', darkBgClass: 'bg-teal-700', tailBorderClass: 'border-t-teal-500' },
  { colorHex: '#f97316', bgClass: 'bg-orange-500', darkBgClass: 'bg-orange-700', tailBorderClass: 'border-t-orange-500' },
]

const SELECTED_JOB_MAP_PADDING = { top: 88, right: 88, bottom: 88, left: 88 }
const SELECTED_JOB_MAX_ZOOM = 16

type LocationSearchOption = {
  id: string
  label: string
  lat: string
  lon: string
  details: string
  city: string
  address: string
}

type LocationMapMarker = {
  id: string
  place: string
  position: {
    lat: number
    lng: number
  }
  linkedJobCount: number
  colorHex: string
  darkColorHex: string
  softColorHex: string
  borderColorHex: string
}

type LocationMarkerPaletteEntry = {
  colorHex: string
  darkColorHex: string
  softColorHex: string
  borderColorHex: string
}

const LOCATION_MARKER_PALETTE: LocationMarkerPaletteEntry[] = [
  { colorHex: '#2563eb', darkColorHex: '#1d4ed8', softColorHex: '#dbeafe', borderColorHex: '#93c5fd' },
  { colorHex: '#dc2626', darkColorHex: '#b91c1c', softColorHex: '#fee2e2', borderColorHex: '#fca5a5' },
  { colorHex: '#059669', darkColorHex: '#047857', softColorHex: '#d1fae5', borderColorHex: '#6ee7b7' },
  { colorHex: '#d97706', darkColorHex: '#b45309', softColorHex: '#fef3c7', borderColorHex: '#fcd34d' },
  { colorHex: '#7c3aed', darkColorHex: '#6d28d9', softColorHex: '#ede9fe', borderColorHex: '#c4b5fd' },
  { colorHex: '#db2777', darkColorHex: '#be185d', softColorHex: '#fce7f3', borderColorHex: '#f9a8d4' },
  { colorHex: '#0f766e', darkColorHex: '#115e59', softColorHex: '#ccfbf1', borderColorHex: '#5eead4' },
  { colorHex: '#ea580c', darkColorHex: '#c2410c', softColorHex: '#ffedd5', borderColorHex: '#fdba74' },
]

const hashLocationColorIndex = (value: string) => {
  let hash = 0
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash * 31) + value.charCodeAt(index)) >>> 0
  }
  return hash
}

const getLocationMarkerPaletteEntry = (locationId: string | number): LocationMarkerPaletteEntry => {
  const normalized = String(locationId).trim()
  const hash = hashLocationColorIndex(normalized || 'location')
  return LOCATION_MARKER_PALETTE[hash % LOCATION_MARKER_PALETTE.length]
}

const LOCATION_MAP_HEIGHT = 450
const OSM_TILE_SIZE = 256
const OSM_DEFAULT_CENTER = { lat: 13.0827, lng: 80.2707 }
const OSM_MIN_ZOOM = 3
const OSM_MAX_ZOOM = 16

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const projectOsmPoint = (position: { lat: number; lng: number }, zoom: number) => {
  const sinLat = clamp(Math.sin((position.lat * Math.PI) / 180), -0.9999, 0.9999)
  const scale = OSM_TILE_SIZE * 2 ** zoom

  return {
    x: ((position.lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale,
  }
}

const unprojectOsmPoint = (point: { x: number; y: number }, zoom: number) => {
  const scale = OSM_TILE_SIZE * 2 ** zoom
  const lng = (point.x / scale) * 360 - 180
  const n = Math.PI - (2 * Math.PI * point.y) / scale
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))

  return {
    lat,
    lng,
  }
}

const getOsmViewport = (
  points: Array<{ lat: number; lng: number }>,
  fallbackCenter: { lat: number; lng: number },
  width: number,
  height: number,
) => {
  if (points.length === 0) {
    return { center: fallbackCenter, zoom: 14 }
  }

  const lats = points.map((position) => position.lat)
  const lngs = points.map((position) => position.lng)
  const center = {
    lat: (Math.min(...lats) + Math.max(...lats)) / 2,
    lng: (Math.min(...lngs) + Math.max(...lngs)) / 2,
  }

  if (points.length === 1 || (Math.min(...lats) === Math.max(...lats) && Math.min(...lngs) === Math.max(...lngs))) {
    return { center, zoom: 15 }
  }

  const usableWidth = Math.max(width - 100, 260)
  const usableHeight = Math.max(height - 100, 260)

  for (let zoom = OSM_MAX_ZOOM; zoom >= OSM_MIN_ZOOM; zoom -= 1) {
    const projected = points.map((position) => projectOsmPoint(position, zoom))
    const xs = projected.map((point) => point.x)
    const ys = projected.map((point) => point.y)
    if (Math.max(...xs) - Math.min(...xs) <= usableWidth && Math.max(...ys) - Math.min(...ys) <= usableHeight) {
      return { center, zoom }
    }
  }

  return { center, zoom: OSM_MIN_ZOOM }
}

function OpenStreetMapLocationView({
  markerPosition,
  workerGroups,
  permanentWorkerGroups,
  locationMarkers,
  geofencePoints,
  editable,
  isDrawing,
  workerMapPin,
  getWorkerMapBadge,
  lockViewportToMarker = false,
  showJobLocationPulse = false,
  jobLocationLabel,
  jobLocationAddress,
  viewportRadiusKm,
  mapHeight = LOCATION_MAP_HEIGHT,
  onMapPointClick,
  onLocationMarkerClick,
  showPrimaryMarker = true,
}: {
  markerPosition: { lat: number; lng: number }
  workerGroups: LocationWorkerMarkerGroup[]
  permanentWorkerGroups: LocationWorkerMarkerGroup[]
  locationMarkers: Array<LocationMapMarker & { isSelected?: boolean }>
  geofencePoints: [number, number][]
  editable: boolean
  isDrawing: boolean
  workerMapPin: { label: string; count: number; isOnline: boolean; bgClass?: string; darkBgClass?: string; tailBorderClass?: string; colorHex?: string } | null
  getWorkerMapBadge: (worker: Worker) => WorkerMapBadge | null
  lockViewportToMarker?: boolean
  showJobLocationPulse?: boolean
  jobLocationLabel?: string
  jobLocationAddress?: string
  viewportRadiusKm?: number
  mapHeight?: number
  onMapPointClick: (lat: number, lng: number) => void
  onLocationMarkerClick?: (locationId: string) => void
  showPrimaryMarker?: boolean
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const dragStartRef = useRef<{ pointerId: number; x: number; y: number; panX: number; panY: number } | null>(null)
  const dragMovedRef = useRef(false)
  const [containerWidth, setContainerWidth] = useState(900)
  const [zoomOffset, setZoomOffset] = useState(0)
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)

  useEffect(() => {
    const element = containerRef.current
    if (!element) {
      return
    }

    const updateWidth = () => {
      const nextWidth = element.getBoundingClientRect().width
      if (nextWidth > 0) {
        setContainerWidth(nextWidth)
      }
    }

    updateWidth()
    const observer = new ResizeObserver(updateWidth)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const viewportPoints = useMemo(() => {
    if (lockViewportToMarker) {
      const points = [markerPosition]
      if (viewportRadiusKm && viewportRadiusKm > 0) {
        points.push(...getBoundingPointsForRadiusKm(markerPosition, viewportRadiusKm))
      }
      geofencePoints.forEach(([lat, lng]) => points.push({ lat, lng }))
      return points
    }

    const points = [...workerGroups, ...permanentWorkerGroups].map(({ position }) => position)
    locationMarkers.forEach(({ position }) => points.push(position))
    geofencePoints.forEach(([lat, lng]) => points.push({ lat, lng }))
    points.push(markerPosition)
    return points
  }, [geofencePoints, locationMarkers, lockViewportToMarker, markerPosition, permanentWorkerGroups, viewportRadiusKm, workerGroups])
  useEffect(() => {
    setZoomOffset(0)
    setPanOffset({ x: 0, y: 0 })
  }, [viewportPoints])

  const baseViewport = useMemo(
    () => getOsmViewport(viewportPoints, markerPosition || OSM_DEFAULT_CENTER, containerWidth, mapHeight),
    [containerWidth, mapHeight, markerPosition, viewportPoints],
  )
  const viewport = useMemo(() => ({
    ...baseViewport,
    zoom: clamp(baseViewport.zoom + zoomOffset, OSM_MIN_ZOOM, OSM_MAX_ZOOM),
  }), [baseViewport, zoomOffset])
  const centerPoint = useMemo(() => projectOsmPoint(viewport.center, viewport.zoom), [viewport.center, viewport.zoom])
  const visibleCenterPoint = useMemo(() => ({
    x: centerPoint.x - panOffset.x,
    y: centerPoint.y - panOffset.y,
  }), [centerPoint, panOffset])
  const tilesPerAxis = 2 ** viewport.zoom
  const minTileX = Math.floor((visibleCenterPoint.x - containerWidth / 2) / OSM_TILE_SIZE)
  const maxTileX = Math.floor((visibleCenterPoint.x + containerWidth / 2) / OSM_TILE_SIZE)
  const minTileY = Math.floor((visibleCenterPoint.y - mapHeight / 2) / OSM_TILE_SIZE)
  const maxTileY = Math.floor((visibleCenterPoint.y + mapHeight / 2) / OSM_TILE_SIZE)
  const tiles = []

  for (let x = minTileX; x <= maxTileX; x += 1) {
    for (let y = minTileY; y <= maxTileY; y += 1) {
      if (y < 0 || y >= tilesPerAxis) {
        continue
      }

      const wrappedX = ((x % tilesPerAxis) + tilesPerAxis) % tilesPerAxis
      tiles.push({
        key: `${viewport.zoom}-${x}-${y}`,
        url: `https://tile.openstreetmap.org/${viewport.zoom}/${wrappedX}/${y}.png`,
        left: x * OSM_TILE_SIZE - visibleCenterPoint.x + containerWidth / 2,
        top: y * OSM_TILE_SIZE - visibleCenterPoint.y + mapHeight / 2,
      })
    }
  }

  const toScreenPoint = (position: { lat: number; lng: number }) => {
    const point = projectOsmPoint(position, viewport.zoom)
    return {
      x: point.x - visibleCenterPoint.x + containerWidth / 2,
      y: point.y - visibleCenterPoint.y + mapHeight / 2,
    }
  }

  const polygonPoints = geofencePoints
    .map(([lat, lng]) => toScreenPoint({ lat, lng }))
    .map((point) => `${point.x},${point.y}`)
    .join(' ')

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden bg-slate-100 ${isPanning ? 'cursor-grabbing' : editable && isDrawing ? 'cursor-crosshair' : 'cursor-grab'}`}
      style={{ height: `${mapHeight}px` }}
      role="application"
      aria-label="Locations on OpenStreetMap"
      onPointerDown={(event) => {
        if (event.button !== 0) {
          return
        }
        event.currentTarget.setPointerCapture(event.pointerId)
        dragMovedRef.current = false
        dragStartRef.current = {
          pointerId: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          panX: panOffset.x,
          panY: panOffset.y,
        }
        setIsPanning(true)
      }}
      onPointerMove={(event) => {
        const dragStart = dragStartRef.current
        if (!dragStart || dragStart.pointerId !== event.pointerId) {
          return
        }

        const deltaX = event.clientX - dragStart.x
        const deltaY = event.clientY - dragStart.y
        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
          dragMovedRef.current = true
        }
        setPanOffset({
          x: dragStart.panX + deltaX,
          y: dragStart.panY + deltaY,
        })
      }}
      onPointerUp={(event) => {
        if (dragStartRef.current?.pointerId === event.pointerId) {
          dragStartRef.current = null
          setIsPanning(false)
        }
      }}
      onPointerCancel={(event) => {
        if (dragStartRef.current?.pointerId === event.pointerId) {
          dragStartRef.current = null
          setIsPanning(false)
        }
      }}
      onClick={(event) => {
        if (dragMovedRef.current) {
          dragMovedRef.current = false
          return
        }
        if (!editable || !isDrawing) {
          return
        }

        const rect = event.currentTarget.getBoundingClientRect()
        const mapPoint = {
          x: visibleCenterPoint.x + event.clientX - rect.left - containerWidth / 2,
          y: visibleCenterPoint.y + event.clientY - rect.top - mapHeight / 2,
        }
        const coordinate = unprojectOsmPoint(mapPoint, viewport.zoom)
        onMapPointClick(Number(coordinate.lat.toFixed(6)), Number(coordinate.lng.toFixed(6)))
      }}
    >
      {tiles.map((tile) => (
        <img
          key={tile.key}
          src={tile.url}
          alt=""
          draggable={false}
          className="absolute h-64 w-64 select-none"
          style={{ left: tile.left, top: tile.top }}
        />
      ))}

      <svg className="pointer-events-none absolute inset-0 h-full w-full">
        {geofencePoints.length >= 3 ? (
          <polygon points={polygonPoints} fill="rgba(37, 99, 235, 0.2)" stroke="#2563eb" strokeWidth="2" />
        ) : null}
        {geofencePoints.map(([lat, lng], index) => {
          const point = toScreenPoint({ lat, lng })
          return <circle key={`${lat}-${lng}-${index}`} cx={point.x} cy={point.y} r="4" fill="#2563eb" stroke="#ffffff" strokeWidth="2" />
        })}
      </svg>

      {[...permanentWorkerGroups, ...workerGroups].map(({ workers, position, kind }, groupIndex) => {
        const point = toScreenPoint(position)
        const isOnline = workers.some((worker) => isWorkerOnline(worker))
        const firstWorkerBadge = getWorkerMapBadge(workers[0])
        const workerIdsKey = workers
          .map((worker) => String(worker.id ?? 'unknown'))
          .sort()
          .join('-')
        const markerColor = firstWorkerBadge?.colorHex || (
          kind === 'permanent'
            ? '#c026d3'
            : isOnline
              ? '#059669'
              : '#64748b'
        )
        const markerText = workers.length === 1
          ? String(firstWorkerBadge?.number ?? 1)
          : `${firstWorkerBadge?.number ?? workers.length}+${workers.length - 1}`

        return (
          <span
            key={`osm-workers-${kind}-${position.lat}-${position.lng}-${workerIdsKey}-${groupIndex}`}
            title={`${kind === 'permanent' ? 'Permanent' : 'Live'}: ${workers.map((worker) => worker.display_name || `Worker ${worker.id}`).join(', ')}`}
            className="absolute flex h-9 w-9 items-center justify-center text-xs font-bold text-white drop-shadow-md"
            style={{
              left: point.x,
              top: point.y,
              transform: 'translate(-50%, -100%)',
            }}
          >
            <ServiceWorkerMarkerGlyph color={markerColor} />
            <span
              className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full border border-white bg-white px-0.5 text-[10px] font-bold leading-none"
              style={{ color: markerColor }}
            >
              {markerText}
            </span>
          </span>
        )
      })}

      {locationMarkers.map((location) => {
        const point = toScreenPoint(location.position)
        return (
          <button
            key={`osm-location-${location.id}`}
            type="button"
            title={`${location.place} (${location.linkedJobCount} jobs)`}
            className="absolute flex h-9 min-w-9 items-center justify-center rounded-full border-2 border-white px-2 text-xs font-bold text-white shadow-lg"
            style={{
              backgroundColor: location.isSelected ? location.darkColorHex : location.colorHex,
              left: point.x,
              top: point.y,
              transform: 'translate(-50%, -100%)',
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation()
              onLocationMarkerClick?.(location.id)
            }}
          >
            {location.linkedJobCount > 0 ? location.linkedJobCount : <MapPin className="h-4 w-4" />}
          </button>
        )
      })}

      {showJobLocationPulse ? (
        <div
          className="absolute z-10"
          style={{
            left: toScreenPoint(markerPosition).x,
            top: toScreenPoint(markerPosition).y,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <JobLocationPulseMarker
            label={jobLocationLabel}
            address={jobLocationAddress}
          />
        </div>
      ) : showPrimaryMarker ? (
        <span
          className="absolute h-5 w-5 rounded-full border-2 border-white bg-primary shadow"
          style={{
            left: toScreenPoint(markerPosition).x,
            top: toScreenPoint(markerPosition).y,
            transform: 'translate(-50%, -50%)',
          }}
        />
      ) : null}

      {workerMapPin ? (
        <div className="pointer-events-none absolute left-1/2 top-1/2 z-20 flex -translate-x-1/2 -translate-y-full flex-col items-center">
          <div className={`flex items-center gap-2 rounded-full border-4 border-white px-4 py-2 text-sm font-bold text-white shadow-2xl ${workerMapPin.bgClass || (workerMapPin.isOnline ? 'bg-emerald-600' : 'bg-slate-500')}`}>
            <span className={`flex h-8 min-w-8 items-center justify-center rounded-full px-2 text-base text-white ${workerMapPin.darkBgClass || (workerMapPin.isOnline ? 'bg-emerald-800' : 'bg-slate-700')}`}>
              {workerMapPin.count}
            </span>
            <span>{workerMapPin.label}</span>
          </div>
          <div className={`h-0 w-0 border-x-[12px] border-t-[18px] border-x-transparent drop-shadow ${workerMapPin.tailBorderClass || (workerMapPin.isOnline ? 'border-t-emerald-600' : 'border-t-slate-500')}`} />
        </div>
      ) : null}

      <div
        className="absolute left-2 top-2 z-30 overflow-hidden rounded-lg border border-border/70 bg-white shadow-sm"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center text-lg font-semibold text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Zoom in"
          disabled={viewport.zoom >= OSM_MAX_ZOOM}
          onClick={(event) => {
            event.stopPropagation()
            setZoomOffset((current) => Math.min(current + 1, OSM_MAX_ZOOM - baseViewport.zoom))
          }}
        >
          +
        </button>
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center border-t border-border/70 text-lg font-semibold text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Zoom out"
          disabled={viewport.zoom <= OSM_MIN_ZOOM}
          onClick={(event) => {
            event.stopPropagation()
            setZoomOffset((current) => Math.max(current - 1, OSM_MIN_ZOOM - baseViewport.zoom))
          }}
        >
          -
        </button>
      </div>

      <span className="absolute bottom-1 right-1 rounded bg-white/85 px-1.5 py-0.5 text-[10px] text-muted-foreground">
        OpenStreetMap
      </span>
    </div>
  )
}

export type LocationsPageProps = {
  mode?: 'default' | 'live'
}

export default function LocationsPage({ mode = 'default' }: LocationsPageProps) {
  const queryClient = useQueryClient()
  const isLiveMode = mode === 'live'
  const routerLocation = useLocation()
  const isWorkerAssignmentPage = (
    routerLocation.pathname.includes('/job/workerassignment')
    || routerLocation.pathname.includes('/job/recurrencejobworkerassignment')
  )
  const isRecurringWorkerAssignmentPage = routerLocation.pathname.includes('/job/recurrencejobworkerassignment')
  const navigate = useNavigate()
  const currentPost = useAuthStore((state) => state.currentPost)
  const { effectiveCapabilities } = useEffectivePostAccess(currentPost)
  const canAccessHotline = canAccessWorkerHotline(effectiveCapabilities)
  const canUseMaps = canUseMapsForPost(currentPost)
  const serviceInstanceId = getServiceInstanceIdFromSearch(routerLocation.search)
  const workerKindFilter = useMemo<'job' | 'project' | 'event'>(() => {
    if (routerLocation.pathname.startsWith('/event/') || routerLocation.pathname.startsWith('/events/')) {
      return 'event'
    }
    if (routerLocation.pathname.startsWith('/project/') || routerLocation.pathname.startsWith('/projects/')) {
      return 'project'
    }
    return 'job'
  }, [routerLocation.pathname])
  const buildLocationJobsLink = useCallback((locationId: string | number) => (
    withServiceInstanceId(
      buildJobsListPath({
        serviceInstanceId,
        jobLocationId: locationId,
      }),
      serviceInstanceId,
    )
  ), [serviceInstanceId])

  const buildLocationWorkersLink = useCallback((locationId: string | number, onlineOnly = false) => {
    const params = new URLSearchParams()
    if (serviceInstanceId) {
      params.set('service_instance_id', serviceInstanceId)
    }
    params.set('job_location_id', String(locationId))
    if (onlineOnly) {
      params.set('attendance_status', 'online')
    }
    return `/workers?${params.toString()}`
  }, [serviceInstanceId])
  const pageSearchParams = useMemo(() => new URLSearchParams(routerLocation.search), [routerLocation.search])
  const selectedJobId = pageSearchParams.get('job_id') || pageSearchParams.get('job') || ''
  const selectedJobZoomRef = useRef<number | null>(null)
  const returnToPath = useMemo(() => {
    const resolved = resolveReturnToPath(routerLocation.search, '')
    return resolved || null
  }, [routerLocation.search])
  const [selectedLocation, setSelectedLocation] = useState<JobLocation | null>(null)
  const [selectedWorkerMapId, setSelectedWorkerMapId] = useState<string | null>(null)
  const [skillPickerState, setSkillPickerState] = useState<{
    worker: Worker
    skills: WorkerSkill[]
  } | null>(null)
  const [payScalePickerState, setPayScalePickerState] = useState<{
    worker: Worker
    skill: WorkerSkill
    payScales: WorkerPayScale[]
  } | null>(null)
  const [assignmentClashState, setAssignmentClashState] = useState<{
    message: string
    payload: AssignmentCreatePayload
  } | null>(null)
  const [workerMapPin, setWorkerMapPin] = useState<{
    label: string
    count: number
    isOnline: boolean
    bgClass?: string
    darkBgClass?: string
    tailBorderClass?: string
    colorHex?: string
  } | null>(null)
  const [geofencePoints, setGeofencePoints] =
  useState<[number, number][]>([])
  const [isEditMode, setIsEditMode] = useState(false)
  const [workerStatusFilter, setWorkerStatusFilter] = useState<'all' | 'online' | 'offline'>('all')
  const [hotlineOnly, setHotlineOnly] = useState(false)
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [searchBox, setSearchBox] =
    useState<google.maps.places.SearchBox | null>(null)
  const [placeSearchValue, setPlaceSearchValue] = useState('')
  const {
    isLoaded,
    mapsAvailable,
    statusMessage: mapsStatusMessage,
    mapProvider,
  } = useGoogleMapsLoader({ serviceInstanceReference: serviceInstanceId, enabled: canUseMaps })
  const canDisplayMap = canUseMaps && (mapProvider === 'openstreetmap' || mapsAvailable)
  const [markerPosition, setMarkerPosition] = useState({
    lat: 13.0827,
    lng: 80.2707,
  })
  const [placeSearchOptions, setPlaceSearchOptions] = useState<LocationSearchOption[]>([])
  const [isSearchingPlace, setIsSearchingPlace] = useState(false)
  const [isPlaceSearchFocused, setIsPlaceSearchFocused] = useState(false)
  const [jobLocationViewRadiusKm, setJobLocationViewRadiusKm] = useState(DEFAULT_JOB_LOCATION_VIEW_RADIUS_KM)
  const effectiveMapRadiusKm = isLiveMode ? DEFAULT_JOB_LOCATION_VIEW_RADIUS_KM : jobLocationViewRadiusKm
  const [liveJobSearch, setLiveJobSearch] = useState('')
  const [assignmentViewTab, setAssignmentViewTab] = useState<'assign' | 'schedule' | 'conflicts' | 'history'>('assign')
  const [assignmentMethod, setAssignmentMethod] = useState<'same_worker' | 'rotate' | 'per_visit'>('same_worker')
  const [assignmentWorkerSearch, setAssignmentWorkerSearch] = useState('')
  const [assignmentRoleFilter, setAssignmentRoleFilter] = useState('all')
  const [assignmentAvailabilityFilter, setAssignmentAvailabilityFilter] = useState<'all' | 'available' | 'limited'>('all')
  const [selectedAssignmentWorkerIds, setSelectedAssignmentWorkerIds] = useState<string[]>([])

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<LocationFormData>({
    resolver: zodResolver(locationSchema),
    defaultValues,
  })

  const selectedAssignmentId = pageSearchParams.get('assignment_id') || ''
  const isLocationBrowseMode = !isLiveMode && !isWorkerAssignmentPage && !selectedJobId && !selectedAssignmentId

  const { data: locations = [], isLoading, error } = useQuery({
    queryKey: ['job-locations'],
    queryFn: () => locationsApi.list(),
    enabled: canUseMaps || isLocationBrowseMode,
  })

  const { data: attendanceDevices = [] } = useQuery({
    queryKey: ['adms-devices', serviceInstanceId],
    queryFn: () => admsDevicesApi.listDevices(serviceInstanceId!),
    enabled: (canUseMaps || isLocationBrowseMode) && Boolean(serviceInstanceId),
  })
  const [selectedMapLocationId, setSelectedMapLocationId] = useState<string | null>(null)

  const locationMapMarkers = useMemo<LocationMapMarker[]>(() => {
    return locations.flatMap((location) => {
      const lat = toNullableCoordinate(location.latitude)
      const lng = toNullableCoordinate(location.longitude)
      const center = parseCenterLatLng(location.center_latlog)
      const position = (lat !== null && lng !== null) ? { lat, lng } : center
      const paletteEntry = getLocationMarkerPaletteEntry(location.id)

      if (!position) {
        return []
      }

      return [{
        id: String(location.id),
        place: location.place || `Location ${location.id}`,
        position,
        linkedJobCount: Number(location.linked_job_count ?? 0),
        colorHex: paletteEntry.colorHex,
        darkColorHex: paletteEntry.darkColorHex,
        softColorHex: paletteEntry.softColorHex,
        borderColorHex: paletteEntry.borderColorHex,
      }]
    })
  }, [locations])

  const selectedMapLocation = useMemo(() => {
    if (!selectedMapLocationId) {
      return null
    }
    return locations.find((location) => String(location.id) === selectedMapLocationId) ?? null
  }, [locations, selectedMapLocationId])

  useEffect(() => {
    if (!selectedMapLocationId) {
      return
    }
    const stillExists = locations.some((location) => String(location.id) === selectedMapLocationId)
    if (!stillExists) {
      setSelectedMapLocationId(null)
    }
  }, [locations, selectedMapLocationId])

  const selectedMapLocationJobsQuery = useQuery({
    queryKey: ['jobs-by-location', serviceInstanceId ?? 'none', selectedMapLocationId ?? 'none'],
    queryFn: () => jobsApi.getJobs({
      service_instance_id: serviceInstanceId ?? undefined,
      job_location_id: selectedMapLocationId || undefined,
      page: 1,
      page_size: 200,
      ordering: '-scheduled_start',
    }),
    enabled: Boolean(isLocationBrowseMode && selectedMapLocationId),
    staleTime: 30_000,
  })

  const selectedAssignmentQuery = useQuery({
    queryKey: ['assignment', selectedAssignmentId, serviceInstanceId ?? 'none'],
    queryFn: () => assignmentsApi.getAssignment(selectedAssignmentId),
    enabled: Boolean(selectedAssignmentId),
    staleTime: 30_000,
  })

  const selectedAssignmentJobId = selectedAssignmentQuery.data?.job_id ? String(selectedAssignmentQuery.data.job_id) : ''
  const effectiveJobId = selectedJobId || selectedAssignmentJobId
  // Job List -> Location/Live focus view: map markers must show only workers actually
  // assigned to the selected job, not every skill-eligible worker (that eligibility list
  // still powers the separate "assign a worker" panel/page below).
  const isSelectedJobFocusView = Boolean(selectedJobId) && !selectedAssignmentId && !isWorkerAssignmentPage
  const liveDayFromUrl = pageSearchParams.get('live_day')
  const liveSelectedDayKey = useMemo(() => {
    const parsed = parseDateKey(liveDayFromUrl)
    return parsed ? toDateKey(parsed) : toDateKey(new Date())
  }, [liveDayFromUrl])
  const [liveDayWindowStartKey, setLiveDayWindowStartKey] = useState(() => {
    const base = parseDateKey(liveDayFromUrl) ?? new Date()
    base.setHours(0, 0, 0, 0)
    base.setDate(base.getDate() - 1)
    return toDateKey(base)
  })
  const liveDayOptions = useMemo(() => {
    const windowStart = parseDateKey(liveDayWindowStartKey) ?? new Date()
    windowStart.setHours(0, 0, 0, 0)

    return Array.from({ length: 7 }, (_, index) => {
      const date = new Date(windowStart)
      date.setDate(windowStart.getDate() + index)
      const dayKey = toDateKey(date)

      return {
        key: dayKey,
        date,
      }
    })
  }, [liveDayWindowStartKey])

  const shiftLiveDayWindow = useCallback((deltaWeeks: number) => {
    setLiveDayWindowStartKey((current) => {
      const parsed = parseDateKey(current) ?? new Date()
      parsed.setDate(parsed.getDate() + (deltaWeeks * 7))
      return toDateKey(parsed)
    })

    const selectedDate = parseDateKey(liveSelectedDayKey) ?? new Date()
    selectedDate.setDate(selectedDate.getDate() + (deltaWeeks * 7))
    const nextDayKey = toDateKey(selectedDate)

    const params = new URLSearchParams(routerLocation.search)
    params.set('live_day', nextDayKey)
    params.delete('job_id')
    params.delete('assignment_id')

    const nextQuery = params.toString()
    navigate(`${routerLocation.pathname}${nextQuery ? `?${nextQuery}` : ''}${routerLocation.hash || ''}`)
  }, [liveSelectedDayKey, navigate, routerLocation.hash, routerLocation.pathname, routerLocation.search])

  const jumpToLiveCurrentWeek = useCallback(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const windowStart = new Date(today)
    windowStart.setDate(today.getDate() - 1)
    setLiveDayWindowStartKey(toDateKey(windowStart))

    const params = new URLSearchParams(routerLocation.search)
    params.set('live_day', toDateKey(today))
    params.delete('job_id')
    params.delete('assignment_id')

    const nextQuery = params.toString()
    navigate(`${routerLocation.pathname}${nextQuery ? `?${nextQuery}` : ''}${routerLocation.hash || ''}`)
  }, [navigate, routerLocation.hash, routerLocation.pathname, routerLocation.search])

  useEffect(() => {
    const selectedDate = parseDateKey(liveSelectedDayKey)
    const windowStart = parseDateKey(liveDayWindowStartKey)

    if (!selectedDate || !windowStart) {
      return
    }

    const windowEnd = new Date(windowStart)
    windowEnd.setDate(windowStart.getDate() + 6)

    if (selectedDate < windowStart || selectedDate > windowEnd) {
      const nextWindowStart = new Date(selectedDate)
      nextWindowStart.setDate(selectedDate.getDate() - 1)
      setLiveDayWindowStartKey(toDateKey(nextWindowStart))
    }
  }, [liveDayWindowStartKey, liveSelectedDayKey])

  const liveJobsQuery = useQuery({
    queryKey: ['live-jobs', serviceInstanceId ?? 'none', liveSelectedDayKey],
    queryFn: () => jobsApi.getJobs({
      service_instance_id: serviceInstanceId ?? undefined,
      page: 1,
      page_size: 200,
      ordering: '-scheduled_start',
      date_filter: 'custom',
      start_date: liveSelectedDayKey,
      end_date: liveSelectedDayKey,
    }),
    enabled: isLiveMode && canUseMaps,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  })

  const liveJobs = useMemo(() => {
    const jobs = (liveJobsQuery.data?.results ?? []).filter((job) => {
      const scheduleDay = getJobScheduleDateKey(job)
      return !scheduleDay || scheduleDay === liveSelectedDayKey
    })
    const activeJobs = jobs.filter((job) => ['draft', 'open', 'in_progress', 'on_hold'].includes(String(job.status)))
    return activeJobs.length > 0 ? activeJobs : jobs
  }, [liveJobsQuery.data?.results, liveSelectedDayKey])

  const liveFilteredJobs = useMemo(() => {
    const query = liveJobSearch.trim().toLowerCase()
    const source = liveJobs

    if (!query) {
      return source
    }

    return source.filter((job) => {
      const fields = [
        job.title,
        job.job_ref,
        job.category_name,
        job.location_info?.place,
        job.client_name,
        job.order_reference_id,
      ]
      return fields.some((field) => String(field ?? '').toLowerCase().includes(query))
    })
  }, [liveJobSearch, liveJobs])

  const selectLiveJob = useCallback((jobId: string | null) => {
    const params = new URLSearchParams(routerLocation.search)
    const nextJobId = jobId && String(jobId) === String(selectedJobId) ? null : jobId

    if (nextJobId && mapRef) {
      selectedJobZoomRef.current = mapRef.getZoom() ?? null
    }

    if (nextJobId) {
      params.set('job_id', nextJobId)
    } else {
      params.delete('job_id')
    }
    params.delete('assignment_id')

    const nextQuery = params.toString()
    navigate(`${routerLocation.pathname}${nextQuery ? `?${nextQuery}` : ''}${routerLocation.hash || ''}`)
  }, [mapRef, navigate, routerLocation.hash, routerLocation.pathname, routerLocation.search, selectedJobId])

  const selectLiveDay = useCallback((dayKey: string) => {
    const params = new URLSearchParams(routerLocation.search)
    params.set('live_day', dayKey)
    params.delete('job_id')
    params.delete('assignment_id')

    const nextQuery = params.toString()
    navigate(`${routerLocation.pathname}${nextQuery ? `?${nextQuery}` : ''}${routerLocation.hash || ''}`)
  }, [navigate, routerLocation.hash, routerLocation.pathname, routerLocation.search])

  const selectedJobQuery = useQuery({
    queryKey: ['job', effectiveJobId || 'none', serviceInstanceId ?? 'none', 'locations-context'],
    queryFn: () => jobsApi.getJob(effectiveJobId, { service_instance_id: serviceInstanceId ?? undefined }),
    enabled: Boolean(effectiveJobId),
    staleTime: 30_000,
  })

  const recurringAvailabilityWindow = useMemo(() => {
    if (!isRecurringWorkerAssignmentPage || !selectedJobQuery.data) {
      return null
    }

    const start = parseJobDateTime(
      selectedJobQuery.data.scheduled_start ?? selectedJobQuery.data.start_date,
      selectedJobQuery.data.start_time,
    )
    if (!start) {
      return null
    }

    const end = new Date(start.getTime() + (24 * HOUR_IN_MS))
    return {
      start,
      end,
      startDateOnly: toDateKey(start),
      endDateOnly: toDateKey(end),
    }
  }, [isRecurringWorkerAssignmentPage, selectedJobQuery.data])

  const recurringJobsWindowQuery = useQuery({
    queryKey: [
      'recurring-assignment-jobs-window',
      serviceInstanceId ?? 'none',
      recurringAvailabilityWindow?.startDateOnly ?? 'none',
      recurringAvailabilityWindow?.endDateOnly ?? 'none',
    ],
    queryFn: () => jobsApi.getJobs({
      service_instance_id: serviceInstanceId ?? undefined,
      page_size: 500,
      date_filter: 'custom',
      start_date: recurringAvailabilityWindow?.startDateOnly,
      end_date: recurringAvailabilityWindow?.endDateOnly,
      ordering: 'scheduled_start',
    }),
    enabled: Boolean(isRecurringWorkerAssignmentPage && recurringAvailabilityWindow),
    staleTime: 30_000,
  })

  const selectedLiveJob = useMemo(() => {
    if (selectedJobQuery.data) {
      return selectedJobQuery.data
    }

    if (!selectedJobId) {
      return null
    }

    return liveJobs.find((job) => String(job.id) === String(selectedJobId)) ?? null
  }, [liveJobs, selectedJobId, selectedJobQuery.data])

  const selectedAssignmentSkillQuery = useQuery({
    queryKey: ['worker-skill', 'assignment-context', selectedAssignmentQuery.data?.worker_skill ?? 'none'],
    queryFn: () => workersApi.getWorkerSkill(String(selectedAssignmentQuery.data?.worker_skill)),
    enabled: Boolean(selectedAssignmentQuery.data?.worker_skill),
    staleTime: 30_000,
  })

  const selectedSkillRoleId = selectedAssignmentSkillQuery.data?.skill_role
    || undefined

  const selectedAssignmentSkillLabel = useMemo(() => {
    if (!selectedAssignmentId) {
      return null
    }

    return selectedAssignmentQuery.data?.worker_skill_label
      || selectedAssignmentQuery.data?.role_label
      || selectedAssignmentQuery.data?.planner_resource_label
      || null
  }, [selectedAssignmentId, selectedAssignmentQuery.data?.planner_resource_label, selectedAssignmentQuery.data?.role_label, selectedAssignmentQuery.data?.worker_skill_label])

  const selectedAssignmentCategoryLabel = useMemo(() => {
    if (!selectedAssignmentId) {
      return null
    }

    return selectedJobQuery.data?.category_name || null
  }, [selectedAssignmentId, selectedJobQuery.data?.category_name])

  const requiresAssignmentSkill = useMemo(() => {
    if (!selectedAssignmentId) {
      return false
    }

    return Boolean(selectedAssignmentSkillQuery.data?.skill_role)
  }, [selectedAssignmentId, selectedAssignmentSkillQuery.data?.skill_role])

  const serviceInstanceQuery = useQuery({
    queryKey: ['job-locations-service-instance', serviceInstanceId],
    queryFn: () => orgTreeApi.getServiceInstance(serviceInstanceId!),
    enabled: canUseMaps && Boolean(serviceInstanceId),
    refetchOnMount: 'always',
  })

  const serviceInstanceLocation = useMemo(
    () => getServiceInstanceLocation(serviceInstanceQuery.data),
    [serviceInstanceQuery.data],
  )

  const distanceUnitSystem = useMemo(() => (
    resolveDistanceUnitSystem(
      serviceInstanceQuery.data?.country_distance_unit,
      serviceInstanceQuery.data?.country_code,
      serviceInstanceQuery.data?.country_name,
    )
  ), [
    serviceInstanceQuery.data?.country_distance_unit,
    serviceInstanceQuery.data?.country_code,
    serviceInstanceQuery.data?.country_name,
  ])

  const selectedJobLocation = useMemo(
    () => getJobCoordinate(selectedJobQuery.data),
    [selectedJobQuery.data],
  )

  const selectedJobLocationById = useMemo(() => {
    const locationId = String(selectedJobQuery.data?.location_id ?? '').trim()
    if (!locationId) {
      return null
    }

    const matchingLocation = locations.find((location) => String(location.id) === locationId)
    if (!matchingLocation) {
      return null
    }

    const lat = toNullableCoordinate(matchingLocation.latitude)
    const lng = toNullableCoordinate(matchingLocation.longitude)
    if (lat !== null && lng !== null) {
      return { lat, lng }
    }

    return parseCenterLatLng(matchingLocation.center_latlog)
  }, [locations, selectedJobQuery.data?.location_id])

  const mapAnchorLocation = selectedJobLocation ?? selectedJobLocationById ?? serviceInstanceLocation

  const workersLastLocationQuery = useQuery({
    queryKey: ['workers-last-locations', serviceInstanceId, workerKindFilter, selectedJobQuery.data?.category, selectedAssignmentId],
    queryFn: () => workersApi.getWorkers({
      service_instance_id: serviceInstanceId ?? undefined,
      kind: workerKindFilter,
      page_size: 500,
      is_active: true,
      job_category: selectedAssignmentId ? undefined : (selectedJobQuery.data?.category || undefined),
      group_by_worker: true,
    }),
    enabled: (canUseMaps || isLocationBrowseMode) && Boolean(serviceInstanceId),
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: isLocationBrowseMode ? false : 30_000,
  })

  const jobAssignmentsQuery = useQuery({
    queryKey: ['assignments', 'locations-job', effectiveJobId || 'none'],
    queryFn: () => assignmentsApi.getAssignments({ job_id: effectiveJobId, page_size: 500 }),
    enabled: Boolean(effectiveJobId),
    staleTime: 15_000,
  })

  const liveAssignedWorkers = useMemo(() => {
    const workerLookup = new Map((workersLastLocationQuery.data?.results ?? []).map((worker) => [String(worker.id), worker]))
    const latestByWorker = new Map<string, Assignment>()

    ;(jobAssignmentsQuery.data?.results ?? []).forEach((assignment) => {
      const workerId = String(assignment.worker_id ?? '').trim()
      if (!workerId) {
        return
      }

      const previous = latestByWorker.get(workerId)
      if (!previous || Number(assignment.id ?? 0) > Number(previous.id ?? 0)) {
        latestByWorker.set(workerId, assignment)
      }
    })

    const statusOrder = { Onsite: 0, Late: 1, Departed: 2, 'No show': 3 } as const

    return Array.from(latestByWorker.values())
      .map((assignment) => {
        const workerId = String(assignment.worker_id ?? '').trim()
        const worker = workerLookup.get(workerId)
        const status = getLiveWorkerStatus(assignment, worker)

        return {
          id: workerId,
          name: assignment.worker_name || worker?.display_name || `Worker ${workerId}`,
          roleLabel: assignment.worker_skill_label || assignment.role_label || assignment.planner_resource_label || null,
          status,
        }
      })
      .sort((left, right) => {
        const statusDiff = statusOrder[left.status] - statusOrder[right.status]
        if (statusDiff !== 0) {
          return statusDiff
        }

        return left.name.localeCompare(right.name)
      })
  }, [jobAssignmentsQuery.data?.results, workersLastLocationQuery.data?.results])

  const liveAssignedWorkerCounts = useMemo(() => ({
    onsite: liveAssignedWorkers.filter((worker) => worker.status === 'Onsite').length,
    late: liveAssignedWorkers.filter((worker) => worker.status === 'Late').length,
    departed: liveAssignedWorkers.filter((worker) => worker.status === 'Departed').length,
    noShow: liveAssignedWorkers.filter((worker) => worker.status === 'No show').length,
  }), [liveAssignedWorkers])

  const assignmentRoleSummaries = useMemo(() => {
    const summaries = new Map<string, { key: string; label: string; requiredCount: number; assignedCount: number }>()

    ;(jobAssignmentsQuery.data?.results ?? []).forEach((assignment) => {
      const roleLabel = String(
        assignment.worker_skill_label
        || assignment.role_label
        || assignment.planner_resource_label
        || assignment.worker_skill
        || assignment.planner_resource
        || 'Unknown role',
      )
      const roleKey = String(
        assignment.worker_skill
        || assignment.role_label
        || assignment.planner_resource
        || roleLabel,
      )

      const existing = summaries.get(roleKey) ?? {
        key: roleKey,
        label: roleLabel,
        requiredCount: 0,
        assignedCount: 0,
      }

      existing.requiredCount += 1
      if (assignment.worker_id) {
        existing.assignedCount += 1
      }

      summaries.set(roleKey, existing)
    })

    return Array.from(summaries.values()).sort((a, b) => a.label.localeCompare(b.label))
  }, [jobAssignmentsQuery.data?.results])

  const assignmentResourceTotals = useMemo(() => {
    const requiredCount = assignmentRoleSummaries.reduce((sum, summary) => sum + summary.requiredCount, 0)
    const assignedCount = assignmentRoleSummaries.reduce((sum, summary) => sum + summary.assignedCount, 0)
    return {
      requiredCount,
      assignedCount,
      unassignedCount: Math.max(requiredCount - assignedCount, 0),
    }
  }, [assignmentRoleSummaries])

  const showJobLocationPulse = Boolean((selectedJobId || selectedAssignmentId) && mapAnchorLocation)
  const jobLocationLabel = useMemo(
    () => formatJobDisplayId(selectedJobQuery.data, selectedJobId || undefined),
    [selectedJobQuery.data, selectedJobId],
  )

  const selectedJobMarkerInfo = useMemo(() => {
    const job = selectedJobQuery.data
    if (!job) {
      return null
    }

    const title = String(job.title ?? '').trim() || jobLocationLabel

    return {
      title,
      address: resolveJobLocationLabel(job),
    }
  }, [jobLocationLabel, selectedJobQuery.data])

  const jobMarkerTitle = selectedJobMarkerInfo?.title ?? jobLocationLabel

  const workerSkillsQuery = useQuery({
    queryKey: [
      'worker-skills',
      'locations-job',
      selectedJobId || selectedAssignmentId || 'none',
      selectedSkillRoleId ?? 'none',
      selectedJobQuery.data?.category ?? 'none',
      serviceInstanceId ?? 'none',
    ],
    queryFn: () => workersApi.getWorkerSkills({
      page_size: 500,
      is_active: true,
      skill_role: selectedSkillRoleId,
      job_category: selectedAssignmentId ? undefined : (selectedJobQuery.data?.category || undefined),
      service_instance_id: serviceInstanceId ?? undefined,
    }),
    enabled: selectedAssignmentId
      ? Boolean(selectedAssignmentSkillQuery.data?.skill_role)
      : Boolean(selectedJobId && (selectedJobQuery.data || selectedSkillRoleId)),
    staleTime: 30_000,
  })

  const customerGroupsQuery = useQuery({
    queryKey: ['job-location-customer-groups', serviceInstanceId ?? 'none'],
    queryFn: () => orgUsersApi.listGroups({ serviceInstanceId }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 60_000,
  })

  const customerGroupIds = useMemo(() => {
    const groups = customerGroupsQuery.data ?? []
    if (groups.length === 0) {
      return [] as string[]
    }

    const customerGroupKeys = new Set(['retail_customer', 'business_customer', 'customer'])
    const clientFallbackKeys = new Set(['client'])

    const customerIds = groups
      .filter((group) => {
        const codeKey = normalizeOrgUserGroupKey(group.unique_code)
        const titleKey = normalizeOrgUserGroupKey(group.title)
        return customerGroupKeys.has(codeKey) || customerGroupKeys.has(titleKey)
      })
      .map((group) => String(group.id))

    if (customerIds.length > 0) {
      return customerIds
    }

    return groups
      .filter((group) => {
        const codeKey = normalizeOrgUserGroupKey(group.unique_code)
        const titleKey = normalizeOrgUserGroupKey(group.title)
        return clientFallbackKeys.has(codeKey) || clientFallbackKeys.has(titleKey)
      })
      .map((group) => String(group.id))
  }, [customerGroupsQuery.data])

  const { data: rawCustomersData, isLoading: isCustomersLoading } = useQuery({
    queryKey: ['job-location-customers-v2', serviceInstanceId ?? 'none'],
    queryFn: async () => {
      if (!serviceInstanceId) {
        return [] as OrgUser[]
      }

      if (customerGroupIds.length === 0) {
        return [] as OrgUser[]
      }

      const pages = await Promise.all(
        customerGroupIds.map((groupId) => orgUsersApi.listPaginated({
          serviceInstanceId,
          userGroupId: groupId,
          page: 1,
          pageSize: 300,
          includeInactive: false,
          includeCustomerActivity: true,
        })),
      )

      const merged = pages.flatMap((page) => page.results ?? [])
      return dedupeOrgUsersByIdentity(merged)
    },
    enabled: Boolean(serviceInstanceId) && !customerGroupsQuery.isLoading,
    staleTime: 60_000,
  })

  const customers = useMemo(() => {
    if (Array.isArray(rawCustomersData)) {
      return rawCustomersData
    }

    if (
      rawCustomersData
      && typeof rawCustomersData === 'object'
      && Array.isArray((rawCustomersData as { results?: OrgUser[] }).results)
    ) {
      return (rawCustomersData as { results: OrgUser[] }).results
    }

    return [] as OrgUser[]
  }, [rawCustomersData])

  const customerOptions = useMemo(
    () => [
      {
        value: '',
        label: 'Own site (no customer)',
        keywords: 'own site internal company no customer optional',
      },
      ...customers.map((customer) => ({
        value: String(customer.id),
        label: getUserDropdownLabel(customer, customer.id),
        keywords: getUserDropdownKeywords(customer),
      })),
    ],
    [customers],
  )

  const workerLocationMarkers = useMemo(
    () => (workersLastLocationQuery.data?.results ?? [])
      .flatMap((worker) => {
        if (!isWorkerOnline(worker)) {
          return []
        }

        const position = getWorkerLastLocation(worker)
        if (!position) {
          return []
        }

        return [{
          worker,
          position,
          kind: 'live' as const,
        }]
      }),
    [workersLastLocationQuery.data?.results],
  )

  const permanentWorkerLocationMarkers = useMemo(
    () => (workersLastLocationQuery.data?.results ?? [])
      .flatMap((worker) => {
        if (isWorkerOnline(worker)) {
          return []
        }

        const position = getWorkerPermanentLocation(worker)
        if (!position) {
          return []
        }

        return [{
          worker,
          position,
          kind: 'permanent' as const,
        }]
      }),
    [workersLastLocationQuery.data?.results],
  )

  const assignmentsByWorkerId = useMemo(() => {
    const map = new Map<string, Assignment>()
    ;(jobAssignmentsQuery.data?.results ?? []).forEach((assignment) => {
      const workerId = String(assignment.worker_id ?? '').trim()
      if (workerId && !map.has(workerId)) {
        map.set(workerId, assignment)
      }
    })
    return map
  }, [jobAssignmentsQuery.data?.results])

  const workerSkillsByWorkerId = useMemo(() => {
    const map = new Map<string, WorkerSkill[]>()
    ;(workerSkillsQuery.data ?? []).forEach((skill) => {
      const workerId = String(skill.worker ?? '').trim()
      if (!workerId) {
        return
      }
      const existing = map.get(workerId) ?? []
      existing.push(skill)
      map.set(workerId, existing)
    })
    return map
  }, [workerSkillsQuery.data])

  const getWorkerAssignment = useCallback((worker: Worker) => {
    const candidates = [
      worker.worker_id,
      worker.id,
      worker.user_id,
    ].map((value) => String(value ?? '').trim()).filter(Boolean)

    for (const candidate of candidates) {
      const assignment = assignmentsByWorkerId.get(candidate)
      if (assignment) {
        return assignment
      }
    }

    return null
  }, [assignmentsByWorkerId])

  const getEligibleWorkerSkills = useCallback((worker: Worker) => {
    const candidates = [
      worker.worker_id,
      worker.id,
      worker.user_id,
    ].map((value) => String(value ?? '').trim()).filter(Boolean)

    const skills = new Map<string, WorkerSkill>()
    candidates.forEach((candidate) => {
      ;(workerSkillsByWorkerId.get(candidate) ?? []).forEach((skill) => {
        skills.set(String(skill.id), skill)
      })
    })

    return Array.from(skills.values()).filter((skill) => (
      skill.is_active !== false && skillMatchesJobCategory(skill, selectedJobQuery.data)
    ))
  }, [selectedJobQuery.data, workerSkillsByWorkerId])

  const selectedAssignmentSkillTokens = useMemo(() => {
    const value = String(selectedAssignmentSkillLabel ?? '').trim().toLowerCase()
    if (!value) {
      return []
    }

    return value
      .split(/[\s/,-]+/)
      .map((token) => token.trim())
      .filter(Boolean)
  }, [selectedAssignmentSkillLabel])

  const matchesAssignmentWorkerFilter = useCallback((worker: Worker) => {
    if (!selectedAssignmentId) {
      return true
    }

    if (getEligibleWorkerSkills(worker).length > 0) {
      return true
    }

    if (selectedAssignmentSkillTokens.length === 0) {
      return false
    }

    const workerLabels = getWorkerSkillLabels(worker).map((label) => label.toLowerCase())
    return selectedAssignmentSkillTokens.every((token) => workerLabels.some((label) => label.includes(token)))
  }, [getEligibleWorkerSkills, selectedAssignmentId, selectedAssignmentSkillTokens])

  const matchesSelectedJobWorkerFilter = useCallback((worker: Worker) => {
    if (selectedAssignmentId) {
      return matchesAssignmentWorkerFilter(worker)
    }

    if (selectedJobId && workerSkillsQuery.isFetched) {
      return getEligibleWorkerSkills(worker).length > 0
    }

    return true
  }, [getEligibleWorkerSkills, matchesAssignmentWorkerFilter, selectedAssignmentId, selectedJobId, workerSkillsQuery.isFetched])

  const getWorkerDistanceLabel = useCallback((worker: Worker) => {
    const workerLocation = getWorkerPreferredLocation(worker)
    return formatDistance(calculateDistanceKm(mapAnchorLocation, workerLocation), distanceUnitSystem)
  }, [distanceUnitSystem, mapAnchorLocation])

  const displayedWorkers = useMemo(() => {
    const workers = workersLastLocationQuery.data?.results ?? []

    let filtered = workers
    if (selectedAssignmentId) {
      filtered = filtered.filter((worker) => matchesAssignmentWorkerFilter(worker))
    }

    const hotlineFilteredWorkers = hotlineOnly
      ? filtered.filter((worker) => isWorkerHotlineActive(worker))
      : filtered

    if (workerStatusFilter === 'online') {
      return hotlineFilteredWorkers.filter((worker) => isWorkerOnline(worker))
    }

    if (workerStatusFilter === 'offline') {
      return hotlineFilteredWorkers.filter((worker) => !isWorkerOnline(worker))
    }

    return hotlineFilteredWorkers
  }, [
    hotlineOnly,
    matchesAssignmentWorkerFilter,
    selectedAssignmentId,
    workerStatusFilter,
    workersLastLocationQuery.data?.results,
  ])

  const workersWithoutEligibleSkillCount = useMemo(() => {
    if (!selectedJobId || !workerSkillsQuery.isFetched) {
      return 0
    }

    return (workersLastLocationQuery.data?.results ?? []).filter(
      (worker) => getEligibleWorkerSkills(worker).length === 0,
    ).length
  }, [
    getEligibleWorkerSkills,
    selectedJobId,
    workerSkillsQuery.isFetched,
    workersLastLocationQuery.data?.results,
  ])

  const manualAssignmentCreatePath = useMemo(() => {
    const jobId = effectiveJobId || selectedJobId
    if (!jobId) {
      return null
    }

    const currentPath = `${routerLocation.pathname}${routerLocation.search}`
    const scopedPath = returnToPath || currentPath
    const assignmentCreateBasePath = scopedPath.includes('/events/')
      || scopedPath.includes('/event/')
      || scopedPath.includes('/committee/')
      || scopedPath.includes('/committees/')
      || scopedPath.includes('/task/')
      || scopedPath.includes('/tasks/')
      ? resolveEventFamilyAssignmentsCreatePath(scopedPath)
      : '/assignments/create'
    return withServiceInstanceId(
      withReturnTo(
        `${assignmentCreateBasePath}?job_id=${encodeURIComponent(jobId)}`,
        returnToPath || currentPath,
      ),
      serviceInstanceId,
    )
  }, [effectiveJobId, returnToPath, routerLocation.pathname, routerLocation.search, selectedJobId, serviceInstanceId])

  const workerMapBadges = useMemo(() => {
    const badges = new Map<string, WorkerMapBadge>()
    displayedWorkers.forEach((worker, index) => {
      const key = String(worker.id ?? getWorkerIdentifier(worker))
      const paletteColor = WORKER_MAP_BADGE_PALETTE[index % WORKER_MAP_BADGE_PALETTE.length]
      badges.set(key, {
        ...paletteColor,
        number: index + 1,
      })
    })
    return badges
  }, [displayedWorkers])

  const getWorkerMapBadge = useCallback((worker: Worker) => {
    const key = String(worker.id ?? getWorkerIdentifier(worker))
    return workerMapBadges.get(key) ?? null
  }, [workerMapBadges])

  const statusFilteredWorkerLocationMarkers = useMemo(() => {
    const relevantMarkers = workerLocationMarkers.filter(({ worker }) => (
      matchesSelectedJobWorkerFilter(worker)
      && (!isSelectedJobFocusView || Boolean(getWorkerAssignment(worker)))
    ))
    const hotlineFilteredMarkers = hotlineOnly
      ? relevantMarkers.filter(({ worker }) => isWorkerHotlineActive(worker))
      : relevantMarkers

    if (workerStatusFilter === 'online') {
      return hotlineFilteredMarkers.filter(({ worker }) => isWorkerOnline(worker))
    }

    if (workerStatusFilter === 'offline') {
      return hotlineFilteredMarkers.filter(({ worker }) => !isWorkerOnline(worker))
    }

    return hotlineFilteredMarkers
  }, [getWorkerAssignment, hotlineOnly, isSelectedJobFocusView, matchesSelectedJobWorkerFilter, workerLocationMarkers, workerStatusFilter])

  const visibleWorkerLocationMarkers = useMemo(() => {
    if (!selectedWorkerMapId) {
      return statusFilteredWorkerLocationMarkers
    }

    return statusFilteredWorkerLocationMarkers.filter(({ worker }) => (
      String(worker.id ?? getWorkerIdentifier(worker)) === selectedWorkerMapId
    ))
  }, [selectedWorkerMapId, statusFilteredWorkerLocationMarkers])

  const visiblePermanentWorkerLocationMarkers = useMemo(() => {
    const relevantMarkers = permanentWorkerLocationMarkers.filter(({ worker }) => (
      matchesSelectedJobWorkerFilter(worker)
      && (!isSelectedJobFocusView || Boolean(getWorkerAssignment(worker)))
    ))
    const hotlineFilteredMarkers = hotlineOnly
      ? relevantMarkers.filter(({ worker }) => isWorkerHotlineActive(worker))
      : relevantMarkers

    if (!selectedWorkerMapId) {
      return hotlineFilteredMarkers
    }

    return hotlineFilteredMarkers.filter(({ worker }) => (
      String(worker.id ?? getWorkerIdentifier(worker)) === selectedWorkerMapId
    ))
  }, [getWorkerAssignment, hotlineOnly, isSelectedJobFocusView, matchesSelectedJobWorkerFilter, permanentWorkerLocationMarkers, selectedWorkerMapId])

  const workerLocationMarkerGroups = useMemo(() => {
    const groups = new Map<string, LocationWorkerMarkerGroup>()

    ;[...visibleWorkerLocationMarkers, ...visiblePermanentWorkerLocationMarkers].forEach(({ worker, position, kind }) => {
      const key = `${kind}:${position.lat},${position.lng}`
      const existing = groups.get(key)
      if (existing) {
        existing.workers.push(worker)
        return
      }

      groups.set(key, {
        workers: [worker],
        position,
        kind,
      })
    })

    return Array.from(groups.values())
  }, [visiblePermanentWorkerLocationMarkers, visibleWorkerLocationMarkers])

  const permanentWorkerLocationMarkerGroups = useMemo(() => {
    const groups = new Map<string, LocationWorkerMarkerGroup>()

    visiblePermanentWorkerLocationMarkers.forEach(({ worker, position, kind }) => {
      const key = `${kind}:${position.lat},${position.lng}`
      const existing = groups.get(key)
      if (existing) {
        existing.workers.push(worker)
        return
      }

      groups.set(key, {
        workers: [worker],
        position,
        kind,
      })
    })

    return Array.from(groups.values())
  }, [visiblePermanentWorkerLocationMarkers])

  const anchorMapToLocation = useCallback((lat: number, lng: number, zoom = 17) => {
    const nextPosition = {
      lat: Number(lat.toFixed(6)),
      lng: Number(lng.toFixed(6)),
    }
    setMarkerPosition(nextPosition)
    mapRef?.panTo(nextPosition)
    mapRef?.setZoom(zoom)
  }, [mapRef])

  const fitMapToMarkers = useCallback((
    markers: LocationWorkerMarker[],
  ) => {
    if (markers.length === 0) {
      return
    }

    const firstPosition = markers[0].position
    const allWorkersAtSamePosition = markers.every(({ position }) => (
      position.lat === firstPosition.lat && position.lng === firstPosition.lng
    ))

    if (markers.length === 1 || allWorkersAtSamePosition) {
      const hasOnlineWorker = markers.some(({ worker, kind }) => kind === 'live' && isWorkerOnline(worker))

      setWorkerMapPin({
        label: markers.length === 1
          ? markers[0].worker.display_name || `Worker ${markers[0].worker.id}`
          : `${markers.length} workers`,
        count: markers.length === 1
          ? (getWorkerMapBadge(markers[0].worker)?.number ?? 1)
          : markers.length,
        isOnline: hasOnlineWorker,
        bgClass: markers.length === 1 ? (getWorkerMapBadge(markers[0].worker)?.bgClass) : undefined,
        darkBgClass: markers.length === 1 ? (getWorkerMapBadge(markers[0].worker)?.darkBgClass) : undefined,
        tailBorderClass: markers.length === 1 ? (getWorkerMapBadge(markers[0].worker)?.tailBorderClass) : undefined,
        colorHex: markers.length === 1 ? (getWorkerMapBadge(markers[0].worker)?.colorHex) : undefined,
      })
      anchorMapToLocation(
        firstPosition.lat,
        firstPosition.lng,
        15,
      )
      return
    }

    const lats = markers.map(({ position }) => position.lat)
    const lngs = markers.map(({ position }) => position.lng)
    setMarkerPosition({
      lat: Number(((Math.min(...lats) + Math.max(...lats)) / 2).toFixed(6)),
      lng: Number(((Math.min(...lngs) + Math.max(...lngs)) / 2).toFixed(6)),
    })

    setWorkerMapPin(null)
    if (mapRef) {
      const bounds = new window.google.maps.LatLngBounds()
      markers.forEach(({ position }) => {
        bounds.extend(position)
      })
      mapRef.fitBounds(bounds)
    }
  }, [anchorMapToLocation, getWorkerMapBadge, mapRef])

  const handleWorkerMapSelect = useCallback((worker: Worker) => {
    const position = getWorkerPreferredLocation(worker)
    if (!position) {
      toast.error('This worker does not have a location for their current status.')
      return
    }

    const workerKey = String(worker.id ?? getWorkerIdentifier(worker))
    if (selectedWorkerMapId === workerKey) {
      setSelectedWorkerMapId(null)
      setWorkerMapPin(null)
      fitMapToMarkers(statusFilteredWorkerLocationMarkers)
      return
    }

    setSelectedWorkerMapId(workerKey)
    const workerBadge = getWorkerMapBadge(worker)
    setWorkerMapPin({
      label: worker.display_name || `Worker ${worker.id}`,
      count: workerBadge?.number ?? 1,
      isOnline: isWorkerOnline(worker),
      bgClass: workerBadge?.bgClass,
      darkBgClass: workerBadge?.darkBgClass,
      tailBorderClass: workerBadge?.tailBorderClass,
      colorHex: workerBadge?.colorHex,
    })
    anchorMapToLocation(position.lat, position.lng, 16)
  }, [anchorMapToLocation, fitMapToMarkers, getWorkerMapBadge, selectedWorkerMapId, statusFilteredWorkerLocationMarkers])

  const fitMapToWorkerMarkers = useCallback(() => {
    setSelectedWorkerMapId(null)
    fitMapToMarkers([...statusFilteredWorkerLocationMarkers, ...permanentWorkerLocationMarkers])
  }, [fitMapToMarkers, permanentWorkerLocationMarkers, statusFilteredWorkerLocationMarkers])

  const fitMapToServiceInstance = useCallback(() => {
    if (!mapAnchorLocation) {
      return
    }

    setWorkerMapPin(null)
    anchorMapToLocation(mapAnchorLocation.lat, mapAnchorLocation.lng, 15)
  }, [anchorMapToLocation, mapAnchorLocation])

  const fitMapToLocationMarkers = useCallback(() => {
    if (locationMapMarkers.length === 0) {
      fitMapToServiceInstance()
      return
    }

    if (locationMapMarkers.length === 1) {
      const location = locationMapMarkers[0]
      anchorMapToLocation(location.position.lat, location.position.lng, 15)
      return
    }

    if (!mapRef || !window.google) {
      const first = locationMapMarkers[0]
      anchorMapToLocation(first.position.lat, first.position.lng, 12)
      return
    }

    const bounds = new window.google.maps.LatLngBounds()
    locationMapMarkers.forEach(({ position }) => {
      bounds.extend(position)
    })
    mapRef.fitBounds(bounds, { top: 80, right: 80, bottom: 80, left: 80 })
  }, [anchorMapToLocation, fitMapToServiceInstance, locationMapMarkers, mapRef])

  const handleMapLocationSelect = useCallback((locationId: string) => {
    setSelectedWorkerMapId(null)
    setWorkerMapPin(null)

    const selected = locationMapMarkers.find((location) => location.id === locationId)

    setSelectedMapLocationId((current) => {
      if (current === locationId) {
        fitMapToLocationMarkers()
        return null
      }

      if (selected) {
        anchorMapToLocation(selected.position.lat, selected.position.lng, 16)
      }

      return locationId
    })
  }, [anchorMapToLocation, fitMapToLocationMarkers, locationMapMarkers])

  const fitMapToRadiusAround = useCallback((center: { lat: number; lng: number }, radiusKm: number) => {
    if (!mapRef || !window.google) {
      return
    }

    setWorkerMapPin(null)
    setMarkerPosition(center)

    const radiusMiles = radiusKm * KM_TO_MILES
    // Calculate bounding box: 1 degree latitude ≈ 69 miles, longitude varies by latitude
    const latDelta = radiusMiles / 69
    const lngDelta = radiusMiles / (69 * Math.cos((center.lat * Math.PI) / 180))

    const bounds = new window.google.maps.LatLngBounds(
      { lat: center.lat - latDelta, lng: center.lng - lngDelta },
      { lat: center.lat + latDelta, lng: center.lng + lngDelta },
    )

    mapRef.fitBounds(bounds, { top: 80, right: 80, bottom: 80, left: 80 })
  }, [mapRef])

  const panMapToLocation = useCallback((position: { lat: number; lng: number }) => {
    const currentZoom = selectedJobZoomRef.current ?? mapRef?.getZoom()
    setMarkerPosition(position)
    mapRef?.panTo(position)
    if (currentZoom !== undefined) {
      mapRef?.setZoom(currentZoom)
    }
    selectedJobZoomRef.current = null
  }, [mapRef])

  const fitMapToSelectedJobAndWorkers = useCallback((
    jobLocation: { lat: number; lng: number },
    workerMarkers: LocationWorkerMarker[],
  ) => {
    if (!mapRef || !window.google || workerMarkers.length === 0) {
      panMapToLocation(jobLocation)
      return
    }

    setMarkerPosition(jobLocation)
    setWorkerMapPin(null)

    const bounds = new window.google.maps.LatLngBounds()
    bounds.extend(jobLocation)
    workerMarkers.forEach(({ position }) => bounds.extend(position))
    mapRef.fitBounds(bounds, SELECTED_JOB_MAP_PADDING)

    const clampSelectedJobZoom = () => {
      const fittedZoom = mapRef.getZoom()
      if (fittedZoom !== undefined && fittedZoom > SELECTED_JOB_MAX_ZOOM) {
        mapRef.setZoom(SELECTED_JOB_MAX_ZOOM)
      }
      window.google.maps.event.removeListener(idleListener)
    }
    const idleListener = window.google.maps.event.addListenerOnce(mapRef, 'idle', clampSelectedJobZoom)
  }, [mapRef, panMapToLocation])

  const fitMapToJobLocation = useCallback(() => {
    if (!mapAnchorLocation) {
      return
    }

    setWorkerMapPin(null)
    setMarkerPosition(mapAnchorLocation)
    fitMapToRadiusAround(mapAnchorLocation, effectiveMapRadiusKm)
  }, [effectiveMapRadiusKm, fitMapToRadiusAround, mapAnchorLocation])

  // The job location pin is created imperatively (rather than as a <Marker> child of
  // <GoogleMap>) because a manually-added Marker element placed among the worker Marker
  // children never painted in this app's setup — the marker attached to the map
  // (getMap() !== null, visible === true) but no pixels were ever drawn for it, even with
  // stable keys and no custom icon. Managing it directly via the Maps JS API sidesteps
  // whatever react-google-maps/api reconciliation issue causes that.
  useEffect(() => {
    if (mapProvider === 'openstreetmap' || !mapRef || !window.google || !showJobLocationPulse || !mapAnchorLocation) {
      return
    }

    const marker = new window.google.maps.Marker({
      position: mapAnchorLocation,
      map: mapRef,
      title: selectedJobMarkerInfo?.address || '',
      zIndex: 1000,
      icon: {
        url: getJobLocationPulseMarkerUrl(),
        scaledSize: new window.google.maps.Size(JOB_LOCATION_MARKER_SIZE, JOB_LOCATION_MARKER_SIZE),
        anchor: new window.google.maps.Point(JOB_LOCATION_MARKER_SIZE / 2, JOB_LOCATION_MARKER_SIZE / 2),
        labelOrigin: new window.google.maps.Point(JOB_LOCATION_MARKER_SIZE / 2, JOB_LOCATION_MARKER_SIZE + 10),
      },
      label: {
        text: jobMarkerTitle || 'Job',
        color: '#e11d48',
        fontSize: '12px',
        fontWeight: '700',
      },
    })

    return () => {
      marker.setMap(null)
    }
  }, [jobMarkerTitle, mapAnchorLocation, mapProvider, mapRef, selectedJobMarkerInfo, showJobLocationPulse])

  // Worker markers are created imperatively for the same reason as the job pin above: the
  // <Marker> JSX children for workers can silently fail to paint the very first time they're
  // attached after navigating to this page (they're still logically present — the Workers
  // panel list, which reads the same query data, renders correctly), and only a later
  // re-render (e.g. toggling the Online/Offline/All filter) reliably makes them appear.
  // Managing them directly via the Maps JS API removes that dependency on a lucky re-render.
  useEffect(() => {
    if (mapProvider === 'openstreetmap' || !mapRef || !window.google) {
      return
    }

    const markers = [
      ...permanentWorkerLocationMarkerGroups.map((group) => ({ group, zIndex: 998, fallbackColor: '#c026d3' })),
      ...workerLocationMarkerGroups.map((group) => ({ group, zIndex: 999, fallbackColor: '#059669' })),
    ].map(({ group: { workers, position }, zIndex, fallbackColor }) => {
      const markerColor = getWorkerMapBadge(workers[0])?.colorHex || fallbackColor
      return new window.google.maps.Marker({
        position,
        map: mapRef,
        title: workers.map((worker) => worker.display_name || `Worker ${worker.id}`).join(', '),
        zIndex,
        icon: {
          url: getServiceWorkerMarkerUrl(markerColor),
          scaledSize: new window.google.maps.Size(36, 44),
          anchor: new window.google.maps.Point(18, 42),
          labelOrigin: new window.google.maps.Point(18, -4),
        },
        label: {
          text: workers.length === 1
            ? String(getWorkerMapBadge(workers[0])?.number ?? 1)
            : `${getWorkerMapBadge(workers[0])?.number ?? workers.length}+${workers.length - 1}`,
          color: markerColor,
          fontSize: '12px',
          fontWeight: '700',
        },
      })
    })

    return () => {
      markers.forEach((marker) => marker.setMap(null))
    }
  }, [getWorkerMapBadge, mapProvider, mapRef, permanentWorkerLocationMarkerGroups, workerLocationMarkerGroups])

  useEffect(() => {
    if (!mapAnchorLocation) {
      return
    }

    if (selectedJobId) {
      setPlaceSearchValue(formatJobDisplayId(selectedJobQuery.data, selectedJobId))
      return
    }

    setPlaceSearchValue((current) => (current.trim() ? current : ''))
  }, [mapAnchorLocation, selectedJobId, selectedJobQuery.data])

  useEffect(() => {
    if (!canUseMaps) {
      setPlaceSearchOptions([])
      setIsSearchingPlace(false)
      return
    }

    if (mapProvider !== 'openstreetmap') {
      setPlaceSearchOptions([])
      setIsSearchingPlace(false)
      return
    }

    const query = placeSearchValue.trim()
    if (!isPlaceSearchFocused || query.length < 3 || !serviceInstanceId) {
      setPlaceSearchOptions([])
      setIsSearchingPlace(false)
      return
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setIsSearchingPlace(true)
        const payload = await orgTreeApi.searchServiceInstanceLocation(serviceInstanceId, query, 5)
        setPlaceSearchOptions(
          (payload.results ?? [])
            .map((entry) => ({
              id: String(entry.id ?? '').trim(),
              label: String(entry.label ?? '').trim(),
              lat: String(entry.lat ?? '').trim(),
              lon: String(entry.lon ?? '').trim(),
              city: getMapSearchCity(entry),
              address: getMapSearchAddress(entry),
              details: buildMapSearchDetails(entry),
            }))
            .filter((entry) => entry.id && entry.label && entry.lat && entry.lon),
        )
      } catch {
        setPlaceSearchOptions([])
      } finally {
        setIsSearchingPlace(false)
      }
    }, 350)

    return () => window.clearTimeout(timeoutId)
  }, [canUseMaps, isPlaceSearchFocused, mapProvider, placeSearchValue, serviceInstanceId])

  const handlePlaceSearchChanged = useCallback(() => {
    const place = searchBox?.getPlaces()?.[0]
    const selectedPlace = place?.geometry?.location

    if (!selectedPlace) {
      return
    }

    const lat = Number(selectedPlace.lat().toFixed(6))
    const lng = Number(selectedPlace.lng().toFixed(6))
    const placeLabel = place.formatted_address || place.name || ''
    const city = getGooglePlaceCity(place)

    if (!isEditMode) {
      setIsEditMode(true)
    }

    if (placeLabel) {
      setPlaceSearchValue(placeLabel)
      setValue('place', city || placeLabel, { shouldDirty: true, shouldValidate: true })
    }
    setValue('latitude', String(lat), { shouldDirty: true, shouldValidate: true })
    setValue('longitude', String(lng), { shouldDirty: true, shouldValidate: true })
    anchorMapToLocation(lat, lng)
  }, [anchorMapToLocation, searchBox, setValue])

  const selectPlaceSearchOption = useCallback((option: LocationSearchOption) => {
    const lat = toNullableCoordinate(option.lat)
    const lng = toNullableCoordinate(option.lon)
    if (lat === null || lng === null) {
      return
    }

    if (!isEditMode) {
      setIsEditMode(true)
    }

    const placeLabel = option.city || option.address || option.label
    setPlaceSearchValue(option.address || option.label)
    setIsPlaceSearchFocused(false)
    setPlaceSearchOptions([])
    setValue('place', placeLabel, { shouldDirty: true, shouldValidate: true })
    setValue('latitude', String(lat), { shouldDirty: true, shouldValidate: true })
    setValue('longitude', String(lng), { shouldDirty: true, shouldValidate: true })
    anchorMapToLocation(lat, lng)
  }, [anchorMapToLocation, isEditMode, setValue])

  const handlePlaceSearchBlur = useCallback(() => {
    setIsPlaceSearchFocused(false)
    setPlaceSearchOptions([])

    if (placeSearchValue.trim() || !mapAnchorLocation) {
      return
    }

    fitMapToServiceInstance()
  }, [fitMapToServiceInstance, mapAnchorLocation, placeSearchValue])

  const addGeofencePoint = useCallback((lat: number, lng: number) => {
    const nextPoint: [number, number] = [
      Number(lat.toFixed(6)),
      Number(lng.toFixed(6)),
    ]

    setGeofencePoints((current) => {
      if (current.length >= MAX_GEOFENCE_POINTS) {
        setIsDrawing(false)
        return current
      }

      const nextPoints = [...current, nextPoint]
      if (nextPoints.length >= MAX_GEOFENCE_POINTS) {
        setIsDrawing(false)
      }
      return nextPoints
    })
  }, [])

  const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (!isEditMode || !isDrawing || !event.latLng) {
      return
    }

    addGeofencePoint(event.latLng.lat(), event.latLng.lng())
  }, [addGeofencePoint, isDrawing, isEditMode])

  const handleOpenStreetMapClick = useCallback((lat: number, lng: number) => {
    if (!isEditMode || !isDrawing) {
      return
    }

    addGeofencePoint(lat, lng)
  }, [addGeofencePoint, isDrawing, isEditMode])

  useEffect(() => {
    if (!selectedLocation) {
      return
    }

    const nextSelected = locations.find((location) => String(location.id) === String(selectedLocation.id)) ?? null
    setSelectedLocation(nextSelected)
  }, [locations, selectedLocation])

  useEffect(() => {
    if (!selectedLocation) {
      reset(defaultValues)
      setGeofencePoints([])
      if (statusFilteredWorkerLocationMarkers.length === 0 && mapAnchorLocation) {
        anchorMapToLocation(mapAnchorLocation.lat, mapAnchorLocation.lng, 15)
      }
      return
    }

    const normalizedPoints = toGeofencePoints(selectedLocation.coordinates)
    const center = calculateCenter(normalizedPoints)
    const anchorLat = selectedLocation.latitude != null ? Number(selectedLocation.latitude) : center?.[0] ?? mapAnchorLocation?.lat
    const anchorLng = selectedLocation.longitude != null ? Number(selectedLocation.longitude) : center?.[1] ?? mapAnchorLocation?.lng

    reset({
      client_user: selectedLocation.client_user ? String(selectedLocation.client_user) : '',
      place: selectedLocation.place,
      latitude: selectedLocation.latitude != null ? String(selectedLocation.latitude) : '',
      longitude: selectedLocation.longitude != null ? String(selectedLocation.longitude) : '',
      attendance_device: selectedLocation.attendance_device ? String(selectedLocation.attendance_device) : '',
    })
    setGeofencePoints(normalizedPoints)
    if (Number.isFinite(anchorLat) && Number.isFinite(anchorLng)) {
      anchorMapToLocation(Number(anchorLat), Number(anchorLng))
    }
  }, [anchorMapToLocation, mapAnchorLocation, reset, selectedLocation, statusFilteredWorkerLocationMarkers.length])

  useEffect(() => {
    if (selectedLocation || selectedWorkerMapId) {
      return
    }

    if (isLocationBrowseMode) {
      if (selectedMapLocationId) {
        const selected = locationMapMarkers.find((location) => location.id === selectedMapLocationId)
        if (selected) {
          anchorMapToLocation(selected.position.lat, selected.position.lng, 16)
        }
        return
      }

      fitMapToLocationMarkers()
      return
    }

    if (selectedJobId || selectedAssignmentId) {
      if (selectedJobQuery.isLoading || !mapAnchorLocation) {
        return
      }
      setWorkerMapPin(null)
      if (selectedJobId && !workerSkillsQuery.isFetched) {
        return
      }

      if (selectedJobId) {
        fitMapToSelectedJobAndWorkers(
          mapAnchorLocation,
          [...statusFilteredWorkerLocationMarkers, ...visiblePermanentWorkerLocationMarkers],
        )
      } else {
        panMapToLocation(mapAnchorLocation)
      }
      return
    }

    if (!mapRef) {
      return
    }

    const combinedMarkers = [...statusFilteredWorkerLocationMarkers, ...permanentWorkerLocationMarkers]
    if (combinedMarkers.length > 0) {
      fitMapToMarkers(combinedMarkers)
      return
    }

    fitMapToServiceInstance()
  }, [anchorMapToLocation, fitMapToLocationMarkers, fitMapToMarkers, fitMapToSelectedJobAndWorkers, fitMapToServiceInstance, isLocationBrowseMode, locationMapMarkers, mapAnchorLocation, mapRef, panMapToLocation, permanentWorkerLocationMarkers, selectedAssignmentId, selectedJobId, selectedJobQuery.isLoading, selectedLocation, selectedMapLocationId, selectedWorkerMapId, statusFilteredWorkerLocationMarkers, visiblePermanentWorkerLocationMarkers, workerSkillsQuery.isFetched])

  useEffect(() => {
    setSelectedWorkerMapId(null)
    setWorkerMapPin(null)
  }, [workerStatusFilter])

  const refreshJobAssignments = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['assignments'] })
    queryClient.invalidateQueries({ queryKey: ['assignments', 'locations-job', effectiveJobId || 'none'] })
    if (effectiveJobId) {
      queryClient.invalidateQueries({ queryKey: ['job-assignments', effectiveJobId] })
      queryClient.invalidateQueries({ queryKey: ['job', effectiveJobId] })
    }
  }, [effectiveJobId, queryClient])

  const assignmentCreateMutation = useMutation({
    mutationFn: (payload: AssignmentCreatePayload) => assignmentsApi.createAssignment(payload),
    onSuccess: () => {
      toast.success('Worker assigned to job')
      refreshJobAssignments()
      setSkillPickerState(null)
      setPayScalePickerState(null)
      setAssignmentClashState(null)
    },
    onError: (mutationError, payload) => {
      const clashMessage = payload.allow_schedule_clash ? null : extractAssignmentClashMessage(mutationError)
      if (clashMessage) {
        setAssignmentClashState({ message: clashMessage, payload })
        return
      }

      toast.error(getErrorMessage(mutationError))
    },
  })

  const assignmentDeleteMutation = useMutation({
    mutationFn: (assignmentId: string) => assignmentsApi.deleteAssignment(assignmentId),
    onSuccess: () => {
      toast.success('Worker removed from job')
      refreshJobAssignments()
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const assignmentUpdateMutation = useMutation({
    mutationFn: ({ assignmentId, payload }: { assignmentId: string; payload: Record<string, unknown> }) => assignmentsApi.updateAssignment(assignmentId, payload),
    onSuccess: () => {
      toast.success('Assignment updated')
      refreshJobAssignments()
      setSkillPickerState(null)
      setPayScalePickerState(null)
      setAssignmentClashState(null)
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const createAssignmentForSkill = useCallback(async (skill: WorkerSkill, payScale?: WorkerPayScale | null) => {
    const job = selectedJobQuery.data
    if (!job) {
      toast.error('Open this page with a job_id to assign workers.')
      return false
    }

    if (selectedAssignmentId && selectedAssignmentQuery.data) {
      const assignment = selectedAssignmentQuery.data
      const paySource = payScale ?? skill
      const payType = (paySource.pay_type ?? assignment.pay_type ?? 'per_hour') as NonNullable<Assignment['pay_type']>
      const basePay = payScale?.base_pay
        ?? skill.base_pay
        ?? skill.per_hour_pay_override
        ?? skill.day_pay_override
        ?? assignment.pay
        ?? 0

      try {
        await assignmentUpdateMutation.mutateAsync({
          assignmentId: selectedAssignmentId,
          payload: {
            job: assignment.job_id || job.id,
            worker_skill: skill.id,
            status: assignment.status,
            pay_type: payType,
            pay: toOptionalNumber(basePay) ?? 0,
            night_pay: toOptionalNumber(payScale?.night_pay ?? skill.night_pay ?? skill.night_pay_override ?? assignment.night_pay) ?? 0,
            holiday_pay: toOptionalNumber(payScale?.holiday_pay ?? skill.holiday_pay ?? skill.holiday_pay_override ?? assignment.holiday_pay) ?? 0,
            peak_pay: toOptionalNumber(payScale?.peak_pay ?? skill.peak_pay ?? assignment.peak_pay) ?? 0,
            pay_flat: isFlatPayType(payType) ? (toOptionalNumber(basePay) ?? 0) : 0,
            estimated_duration: toOptionalNumber(job.scheduled_hours ?? job.estimated_hours ?? assignment.estimated_duration),
            special_pay: toOptionalNumber(assignment.special_pay) ?? 0,
          },
        })
        return true
      } catch {
        return false
      }
    }

    try {
      await assignmentCreateMutation.mutateAsync(buildAssignmentPayload({ job, skill, payScale }))
      return true
    } catch {
      return false
    }
  }, [assignmentCreateMutation, assignmentUpdateMutation, selectedAssignmentId, selectedAssignmentQuery.data, selectedJobQuery.data])

  const createAssignmentForWorker = useCallback(async (worker: Worker) => {
    const job = selectedJobQuery.data
    if (!job) {
      toast.error('Open this page with a job_id to assign workers.')
      return false
    }

    try {
      await assignmentCreateMutation.mutateAsync(buildWorkerOnlyAssignmentPayload(job, worker))
      return true
    } catch {
      return false
    }
  }, [assignmentCreateMutation, selectedJobQuery.data])

  const resolvePayScaleAndAssign = useCallback(async (worker: Worker, skill: WorkerSkill, options?: { interactive?: boolean }) => {
    const interactive = options?.interactive !== false

    try {
      const payScales = await workersApi.getWorkerPayScales({
        worker_skill: skill.id,
        is_active: true,
        page_size: 200,
        service_instance_id: serviceInstanceId ?? undefined,
      })

      if (payScales.length > 1 && interactive) {
        setPayScalePickerState({ worker, skill, payScales })
        return false
      }

      return await createAssignmentForSkill(skill, payScales[0] ?? null)
    } catch (payScaleError) {
      toast.error(getErrorMessage(payScaleError))
      return false
    }
  }, [createAssignmentForSkill, serviceInstanceId])

  const resolveFallbackSkillAndAssign = useCallback(async (worker: Worker, options?: { interactive?: boolean; skipWorkerSkillLookup?: boolean }) => {
    const interactive = options?.interactive !== false
    const skipWorkerSkillLookup = options?.skipWorkerSkillLookup === true

    try {
      let activeSkills = skipWorkerSkillLookup
        ? []
        : await workersApi.getSkillsByWorker(getWorkerIdentifier(worker), {
          service_instance_id: serviceInstanceId ?? undefined,
          is_active: true,
        })
      activeSkills = activeSkills.filter((skill) => skillMatchesJobCategory(skill, selectedJobQuery.data))

      const preferredSkill = activeSkills.find((skill) => skill.is_primary) ?? activeSkills[0] ?? null
      if (!preferredSkill) {
        if (isRecurringWorkerAssignmentPage) {
          return await createAssignmentForWorker(worker)
        }
        const plannerLinks = (selectedJobQuery.data as { planner_resource_links?: Array<{
          planner_resource?: string | number | null
          planner_resource_id?: string | number | null
          planner_resource_role_name_id?: string | number | null
        }> } | undefined)?.planner_resource_links ?? []
        const fallbackPlannerResourceId = selectedAssignmentQuery.data?.planner_resource
          ?? plannerLinks.find((link) => link.planner_resource_id != null)?.planner_resource_id
          ?? plannerLinks.find((link) => link.planner_resource != null)?.planner_resource
          ?? null
        const fallbackRoleId = selectedSkillRoleId
          ?? selectedAssignmentQuery.data?.role
          ?? plannerLinks.find((link) => link.planner_resource_role_name_id != null)?.planner_resource_role_name_id
          ?? worker.worker_skill_mappings?.find((mapping) => mapping.is_primary && mapping.skill_role_id != null)?.skill_role_id
          ?? worker.worker_skill_mappings?.find((mapping) => mapping.skill_role_id != null)?.skill_role_id
          ?? worker.role

        const jobCategoryId = selectedJobQuery.data?.category
          ?? (selectedJobQuery.data as { category_id?: string | number | null } | undefined)?.category_id

        if (fallbackRoleId != null && String(fallbackRoleId).trim() !== '') {
          try {
            const createdSkill = await workersApi.createWorkerSkill({
              worker: getWorkerIdentifier(worker),
              skill_role: fallbackRoleId,
              job_category: jobCategoryId != null && String(jobCategoryId).trim() ? String(jobCategoryId) : undefined,
              is_active: true,
            })

            return await resolvePayScaleAndAssign(worker, createdSkill, { interactive })
          } catch {
            // Continue to direct assignment fallback when worker-skill creation is not possible.
          }
        }

        if (fallbackPlannerResourceId != null && String(fallbackPlannerResourceId).trim() !== '') {
          const job = selectedJobQuery.data
          if (!job) {
            toast.error('Open this page with a job_id to assign workers.')
            return false
          }

          const payload: AssignmentCreatePayload = {
            job: job.id,
            worker: getWorkerIdentifier(worker),
            planner_resource: fallbackPlannerResourceId,
            status: 'pending',
            pay_type: 'per_hour',
            pay: 0,
            night_pay: 0,
            holiday_pay: 0,
            peak_pay: 0,
            pay_flat: 0,
            estimated_duration: toOptionalNumber(job.scheduled_hours ?? job.estimated_hours) ?? 0,
          }

          await assignmentCreateMutation.mutateAsync(payload)
          return true
        }

        const openAssignments = (jobAssignmentsQuery.data?.results ?? []).filter((assignment) => !assignment.worker_id)
        let openPlannerSlot = openAssignments.find((assignment) => assignment.planner_resource != null) ?? openAssignments[0]

        if (!openPlannerSlot && selectedJobQuery.data?.id) {
          try {
            const unassignedAssignments = await assignmentsApi.getAssignments({
              job_id: String(selectedJobQuery.data.id),
              page_size: 500,
              unassigned: true,
            })
            const remoteOpenAssignments = (unassignedAssignments.results ?? []).filter((assignment) => !assignment.worker_id)
            openPlannerSlot = remoteOpenAssignments.find((assignment) => assignment.planner_resource != null) ?? remoteOpenAssignments[0]
          } catch {
            // Keep existing fallback chain; onError toasts are handled by assignment mutations.
          }
        }

        if (openPlannerSlot) {
          const job = selectedJobQuery.data
          if (!job) {
            toast.error('Open this page with a job_id to assign workers.')
            return false
          }

          await assignmentUpdateMutation.mutateAsync({
            assignmentId: String(openPlannerSlot.id),
            payload: {
              job: openPlannerSlot.job_id || job.id,
              worker: getWorkerIdentifier(worker),
              ...(openPlannerSlot.planner_resource != null ? { planner_resource: openPlannerSlot.planner_resource } : {}),
              status: openPlannerSlot.status || 'pending',
              pay_type: (openPlannerSlot.pay_type || 'per_hour') as NonNullable<Assignment['pay_type']>,
              pay: toOptionalNumber(openPlannerSlot.pay) ?? 0,
              night_pay: toOptionalNumber(openPlannerSlot.night_pay) ?? 0,
              holiday_pay: toOptionalNumber(openPlannerSlot.holiday_pay) ?? 0,
              peak_pay: toOptionalNumber(openPlannerSlot.peak_pay) ?? 0,
              pay_flat: toOptionalNumber(openPlannerSlot.pay_flat) ?? 0,
              estimated_duration: toOptionalNumber(openPlannerSlot.estimated_duration ?? job.scheduled_hours ?? job.estimated_hours) ?? 0,
              special_pay: toOptionalNumber(openPlannerSlot.special_pay) ?? 0,
            },
          })
          return true
        }

        // If we skipped lookup earlier, use a category-matching skill when one exists.
        if (skipWorkerSkillLookup) {
          try {
            activeSkills = (await workersApi.getSkillsByWorker(getWorkerIdentifier(worker), {
              service_instance_id: serviceInstanceId ?? undefined,
              is_active: true,
            })).filter((skill) => skillMatchesJobCategory(skill, selectedJobQuery.data))
            const lateSkill = activeSkills.find((skill) => skill.is_primary) ?? activeSkills[0] ?? null
            if (lateSkill) {
              return await resolvePayScaleAndAssign(worker, lateSkill, { interactive })
            }
          } catch {
            // Workers without a matching skill are still assigned directly.
          }
        }

        return await createAssignmentForWorker(worker)
      }

      return await resolvePayScaleAndAssign(worker, preferredSkill, { interactive })
    } catch (workerSkillsError) {
      toast.error(getErrorMessage(workerSkillsError))
      return false
    }
  }, [assignmentCreateMutation, assignmentUpdateMutation, createAssignmentForWorker, isRecurringWorkerAssignmentPage, jobAssignmentsQuery.data?.results, resolvePayScaleAndAssign, selectedAssignmentQuery.data?.planner_resource, selectedAssignmentQuery.data?.role, selectedJobQuery.data, selectedSkillRoleId, serviceInstanceId])

  const handleAddWorkerToJob = useCallback(async (worker: Worker, options?: { interactive?: boolean; skipWorkerSkillLookup?: boolean }) => {
    const interactive = options?.interactive !== false
    const skipWorkerSkillLookup = options?.skipWorkerSkillLookup ?? !interactive

    if (!selectedJobQuery.data) {
      toast.error('Open this page with a job_id to assign workers.')
      return false
    }

    const existingAssignment = getWorkerAssignment(worker)
    if (existingAssignment && (!selectedAssignmentId || String(existingAssignment.id) !== selectedAssignmentId)) {
      toast.info('This worker is already linked to this job.')
      return false
    }

    const skills = getEligibleWorkerSkills(worker)
    if (skills.length === 0) {
      if (!requiresAssignmentSkill) {
        return await resolveFallbackSkillAndAssign(worker, { interactive, skipWorkerSkillLookup })
      }

      toast.error('No eligible active skill found for this worker and job.')
      return false
    }

    if (skills.length > 1 && interactive) {
      setSkillPickerState({ worker, skills })
      return false
    }

    const preferredSkill = skills.find((skill) => skill.is_primary) ?? skills[0]
    return await resolvePayScaleAndAssign(worker, preferredSkill, { interactive })
  }, [getEligibleWorkerSkills, getWorkerAssignment, requiresAssignmentSkill, resolveFallbackSkillAndAssign, resolvePayScaleAndAssign, selectedAssignmentId, selectedJobQuery.data])

  const canRemoveAssignmentFromJob = useCallback((assignment: Assignment | null) => {
    if (!assignment) {
      return false
    }

    if (resolveAssignmentStatus(assignment.status) === 'pending') {
      return true
    }

    const jobStatus = String(selectedJobQuery.data?.workflow_status_code ?? selectedJobQuery.data?.status ?? '').trim()
    return ['draft', 'scheduled', 'open'].includes(jobStatus)
  }, [selectedJobQuery.data])

  const handleRemoveAssignment = useCallback((assignment: Assignment | null) => {
    if (!assignment) {
      return
    }

    if (!canRemoveAssignmentFromJob(assignment)) {
      toast.error('Only pending assignments or assignments on draft/scheduled jobs can be removed.')
      return
    }

    assignmentDeleteMutation.mutate(String(assignment.id))
  }, [assignmentDeleteMutation, canRemoveAssignmentFromJob])

  const recurringWorkerAvailabilityById = useMemo(() => {
    const metricsByWorker = new Map<string, {
      occupiedSlots: boolean[]
      assignedHours: number
      availabilityPercent: number
      availabilityLabel: 'available' | 'limited'
    }>()

    if (!isRecurringWorkerAssignmentPage || !recurringAvailabilityWindow) {
      return metricsByWorker
    }

    const workers = workersLastLocationQuery.data?.results ?? []
    const jobsInWindow = recurringJobsWindowQuery.data?.results ?? []

    const workerIdAliases = new Map<string, Set<string>>()
    workers.forEach((worker) => {
      const aliases = new Set<string>([
        String(worker.id),
        String(worker.worker_id ?? ''),
        String(worker.user_id ?? ''),
      ].filter((value) => value.trim().length > 0))
      workerIdAliases.set(String(worker.id), aliases)
      metricsByWorker.set(String(worker.id), {
        occupiedSlots: Array.from({ length: 24 }, () => false),
        assignedHours: 0,
        availabilityPercent: isWorkerOnline(worker) ? 100 : 35,
        availabilityLabel: isWorkerOnline(worker) ? 'available' : 'limited',
      })
    })

    const rangeStart = recurringAvailabilityWindow.start.getTime()
    const rangeEnd = recurringAvailabilityWindow.end.getTime()

    jobsInWindow.forEach((job) => {
      const jobAssigneeCandidates = new Set<string>([
        String(job.primary_worker ?? ''),
        String(job.primary_worker_info?.id ?? ''),
      ].filter((value) => value.trim().length > 0))
      if (jobAssigneeCandidates.size === 0) {
        return
      }

      const start = parseJobDateTime(job.scheduled_start ?? job.start_date, job.start_time)
      if (!start) {
        return
      }

      const parsedEnd = parseJobDateTime(job.scheduled_end ?? job.end_date, job.end_time)
      const end = parsedEnd && parsedEnd.getTime() > start.getTime()
        ? parsedEnd
        : new Date(start.getTime() + HOUR_IN_MS)

      const jobStartTime = Math.max(start.getTime(), rangeStart)
      const jobEndTime = Math.min(end.getTime(), rangeEnd)
      if (jobEndTime <= jobStartTime) {
        return
      }

      workers.forEach((worker) => {
        const aliases = workerIdAliases.get(String(worker.id))
        if (!aliases) {
          return
        }

        const isAssignedToWorker = Array.from(jobAssigneeCandidates).some((candidate) => aliases.has(candidate))
        if (!isAssignedToWorker) {
          return
        }

        const existing = metricsByWorker.get(String(worker.id))
        if (!existing) {
          return
        }

        const overlapHours = (jobEndTime - jobStartTime) / HOUR_IN_MS
        existing.assignedHours += Math.max(0, overlapHours)

        for (let index = 0; index < 24; index += 1) {
          const slotStart = rangeStart + (index * HOUR_IN_MS)
          const slotEnd = slotStart + HOUR_IN_MS
          if (jobStartTime < slotEnd && jobEndTime > slotStart) {
            existing.occupiedSlots[index] = true
          }
        }
      })
    })

    metricsByWorker.forEach((value, workerId) => {
      const occupiedHours = value.occupiedSlots.filter(Boolean).length
      const availabilityPercent = Math.max(0, Math.min(100, Math.round(((24 - occupiedHours) / 24) * 100)))
      value.availabilityPercent = availabilityPercent
      value.availabilityLabel = availabilityPercent >= 75 ? 'available' : 'limited'
      metricsByWorker.set(workerId, value)
    })

    return metricsByWorker
  }, [isRecurringWorkerAssignmentPage, recurringAvailabilityWindow, recurringJobsWindowQuery.data?.results, workersLastLocationQuery.data?.results])

  const assignmentSourceWorkers = useMemo(() => {
    if (isRecurringWorkerAssignmentPage) {
      return workersLastLocationQuery.data?.results ?? []
    }
    return displayedWorkers
  }, [displayedWorkers, isRecurringWorkerAssignmentPage, workersLastLocationQuery.data?.results])

  const assignmentRoleFilterOptions = useMemo(() => {
    const labels = new Set<string>()
    assignmentSourceWorkers.forEach((worker) => {
      getWorkerSkillLabels(worker).forEach((label) => {
        if (label.trim()) {
          labels.add(label.trim())
        }
      })
    })
    return ['all', ...Array.from(labels)]
  }, [assignmentSourceWorkers])

  const assignmentWorkerRows = useMemo(() => {
    const normalize = (value: string) => value.trim().toLowerCase()

    return assignmentSourceWorkers
      .map((worker) => {
        const workerId = String(worker.id)
        const assignment = getWorkerAssignment(worker)
        const skillLabels = getWorkerSkillLabels(worker)
        const orgRoleLabel = [
          worker.linked_post_role_name,
          worker.role_name,
          worker.linked_post_label,
          worker.role ? String(worker.role) : '',
        ]
          .map((value) => String(value ?? '').trim())
          .find(Boolean)
        const roleLabel = orgRoleLabel || skillLabels[0] || 'Role not assigned'
        const recurringAvailability = recurringWorkerAvailabilityById.get(workerId)
        const isOnline = isWorkerOnline(worker)
        const estimatedWorkloadHours = recurringAvailability
          ? Math.round(recurringAvailability.assignedHours * 10) / 10
          : (assignment ? 24 : (isOnline ? 0 : 6))
        const availabilityPercent = recurringAvailability
          ? recurringAvailability.availabilityPercent
          : (isOnline ? 100 : 35)
        const availabilityLabel = recurringAvailability
          ? recurringAvailability.availabilityLabel
          : (availabilityPercent >= 75 ? 'available' : 'limited')
        const distanceLabel = getWorkerDistanceLabel(worker) || 'Distance unavailable'

        return {
          id: workerId,
          worker,
          assignment,
          name: worker.display_name || `Worker ${worker.id}`,
          roleLabel,
          workloadLabel: `${estimatedWorkloadHours} hrs`,
          availabilityPercent,
          availabilityLabel,
          distanceLabel,
        }
      })
      .filter((row) => {
        if (assignmentRoleFilter !== 'all' && row.roleLabel !== assignmentRoleFilter) {
          return false
        }
        if (assignmentAvailabilityFilter !== 'all' && row.availabilityLabel !== assignmentAvailabilityFilter) {
          return false
        }
        if (!assignmentWorkerSearch.trim()) {
          return true
        }

        const query = normalize(assignmentWorkerSearch)
        return normalize(`${row.name} ${row.distanceLabel} ${row.roleLabel}`).includes(query)
      })
      .sort((a, b) => b.availabilityPercent - a.availabilityPercent)
  }, [assignmentAvailabilityFilter, assignmentRoleFilter, assignmentWorkerSearch, assignmentSourceWorkers, getWorkerAssignment, getWorkerDistanceLabel, recurringWorkerAvailabilityById])

  const selectedAssignmentWorkerRows = useMemo(() => {
    if (selectedAssignmentWorkerIds.length === 0) {
      return []
    }

    const selectedIdSet = new Set(selectedAssignmentWorkerIds)
    return assignmentWorkerRows.filter((row) => selectedIdSet.has(row.id))
  }, [assignmentWorkerRows, selectedAssignmentWorkerIds])

  useEffect(() => {
    setSelectedAssignmentWorkerIds([])
  }, [selectedJobId])

  const toggleAssignmentWorkerSelection = useCallback((workerId: string) => {
    setSelectedAssignmentWorkerIds((current) => {
      if (current.includes(workerId)) {
        return current.filter((id) => id !== workerId)
      }
      return [...current, workerId]
    })
  }, [])

  const handleAutoSelectAssignmentWorkers = useCallback(() => {
    const suggestedCount = Math.max(1, assignmentResourceTotals.unassignedCount || 1)
    const next = assignmentWorkerRows
      .filter((row) => !row.assignment)
      .slice(0, suggestedCount)
      .map((row) => row.id)

    if (next.length === 0) {
      toast.info('No available workers to auto-select.')
      return
    }
    setSelectedAssignmentWorkerIds(next)
  }, [assignmentResourceTotals.unassignedCount, assignmentWorkerRows])

  const handleSaveAssignmentWorkers = useCallback(async () => {
    if (!selectedJobQuery.data) {
      toast.error('Open this page with a job_id to assign workers.')
      return
    }
    if (selectedAssignmentWorkerIds.length === 0) {
      toast.error('Select at least one worker to assign.')
      return
    }

    const selectedRows = assignmentWorkerRows
      .filter((row) => selectedAssignmentWorkerIds.includes(row.id))

    const assignableRows = selectedRows.filter((row) => !row.assignment)
    const skippedAlreadyAssigned = selectedRows.length - assignableRows.length

    if (assignableRows.length === 0) {
      if (skippedAlreadyAssigned > 0) {
        toast.info('All selected workers are already linked to this job.')
      } else {
        toast.error('Select at least one worker to assign.')
      }
      return
    }

    let processedCount = 0
    for (const worker of assignableRows.map((row) => row.worker)) {
      const assigned = await handleAddWorkerToJob(worker, { interactive: false, skipWorkerSkillLookup: true })
      if (assigned) {
        processedCount += 1
      }
    }

    const failedCount = assignableRows.length - processedCount

    if (processedCount === 0) {
      if (skippedAlreadyAssigned > 0) {
        toast.error(`No new workers were assigned. ${skippedAlreadyAssigned} already linked, ${failedCount} failed.`)
      } else {
        toast.error(`No workers were assigned. ${failedCount} failed. Check skills and assignment context.`)
      }
      return
    }

    if (processedCount < assignableRows.length || skippedAlreadyAssigned > 0) {
      toast.success(`${processedCount} assigned, ${failedCount} failed, ${skippedAlreadyAssigned} already linked.`)
      return
    }

    toast.success('Assignment request processed.')
  }, [assignmentWorkerRows, handleAddWorkerToJob, selectedAssignmentWorkerIds, selectedJobQuery.data])

  const createMutation = useMutation({
    mutationFn: locationsApi.create,
    onSuccess: (location) => {
      toast.success('Location created successfully')
      queryClient.invalidateQueries({ queryKey: ['job-locations'] })
      setSelectedLocation(location)
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Parameters<typeof locationsApi.update>[1] }) => locationsApi.update(id, payload),
    onSuccess: (location) => {
      toast.success('Location updated successfully')
      queryClient.invalidateQueries({ queryKey: ['job-locations'] })
      setSelectedLocation(location)
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: locationsApi.delete,
    onSuccess: () => {
      toast.success('Location deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['job-locations'] })
      setSelectedLocation(null)
      reset(defaultValues)
    },
    onError: (mutationError) => {
      toast.error(getErrorMessage(mutationError))
    },
  })

  const onSubmit = (data: LocationFormData) => {
    if (geofencePoints.length > 0 && geofencePoints.length < 3) {
      toast.error('Add at least 3 map points to create a geofence polygon.')
      return
    }
    if (geofencePoints.length > MAX_GEOFENCE_POINTS) {
      toast.error(`No more than ${MAX_GEOFENCE_POINTS} map points are allowed.`)
      return
    }

    const center = calculateCenter(geofencePoints)

const geofenceCenter: [number, number] | undefined = center
  ? [
      Number(center[0].toFixed(6)),
      Number(center[1].toFixed(6)),
    ]
  : undefined
    const formLatitude = toNumberOrNull(data.latitude)
    const formLongitude = toNumberOrNull(data.longitude)
    const fallbackLatitude = formLatitude ?? geofenceCenter?.[0] ?? mapAnchorLocation?.lat ?? null
    const fallbackLongitude = formLongitude ?? geofenceCenter?.[1] ?? mapAnchorLocation?.lng ?? null

    const payload: JobLocationPayload = {
      client_user: data.client_user?.trim() ? data.client_user.trim() : null,
      place: data.place.trim(),
      latitude: fallbackLatitude,
      longitude: fallbackLongitude,
      coordinates: geofencePoints.length >= 3 ? geofencePoints : null,
      attendance_device: data.attendance_device?.trim() ? data.attendance_device.trim() : null,
    }

    if (fallbackLatitude != null && fallbackLongitude != null) {
      payload.center_latlog = `${fallbackLatitude},${fallbackLongitude}`
    }

    if (selectedLocation) {
      updateMutation.mutate({ id: String(selectedLocation.id), payload })
      return
    }

    createMutation.mutate(payload)
  }

  const startCreate = () => {
    setSelectedLocation(null)
    reset(defaultValues)
    setGeofencePoints([])
    setIsEditMode(true)
    fitMapToServiceInstance()
  }

  const handleCancelEdit = () => {
    if (selectedLocation) {
      reset({
        client_user: selectedLocation.client_user
          ? String(selectedLocation.client_user)
          : '',
        place: selectedLocation.place,
        latitude:
          selectedLocation.latitude != null
            ? String(selectedLocation.latitude)
            : '',
        longitude:
          selectedLocation.longitude != null
            ? String(selectedLocation.longitude)
            : '',
        attendance_device: selectedLocation.attendance_device ? String(selectedLocation.attendance_device) : '',
      })

      setGeofencePoints(
        toGeofencePoints(selectedLocation.coordinates)
      )
    } else {
      reset(defaultValues)
      setGeofencePoints([])
      fitMapToServiceInstance()
    }

    setIsEditMode(false)
  }

  const renderLocationForm = () => (
    <form onSubmit={handleSubmit(onSubmit)} className="rounded-2xl border border-border/70 bg-muted/20 p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-semibold text-foreground">
            {selectedLocation ? 'Edit Location' : 'Add Location'}
          </p>
          <p className="text-xs text-muted-foreground">
            {selectedLocation
              ? 'Update the selected reusable job location.'
              : 'Add a reusable location for the active service branch. Customer is optional for own sites. Blank coordinates use the service instance location.'}
          </p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={handleCancelEdit}>
          <X className="mr-2 h-4 w-4" />
          Close
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="client_user">Customer <span className="font-normal text-muted-foreground">(optional)</span></Label>
          <Controller
            control={control}
            name="client_user"
            render={({ field }) => (
              <SearchableSelect
                id="client_user"
                ariaLabel="Customer (optional)"
                value={field.value ?? ''}
                onValueChange={field.onChange}
                options={customerOptions}
                placeholder={isCustomersLoading ? 'Loading customers...' : 'Own site or select customer'}
                searchPlaceholder="Search customers"
                emptyText={isCustomersLoading ? 'Loading customers...' : 'No customers found.'}
                disabled={isCustomersLoading}
              />
            )}
          />
          <p className="text-xs text-muted-foreground">
            Leave as own site when this location belongs to your organisation, not a customer.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="place">Place</Label>
          <Input id="place" placeholder="Warehouse entrance" {...register('place')} aria-invalid={!!errors.place} />
          {errors.place ? <p className="text-sm text-destructive">{errors.place.message}</p> : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="latitude">Latitude</Label>
          <Input
            id="latitude"
            placeholder={mapAnchorLocation ? String(mapAnchorLocation.lat) : '13.082680'}
            {...register('latitude')}
            aria-invalid={!!errors.latitude}
          />
          {errors.latitude ? <p className="text-sm text-destructive">{errors.latitude.message}</p> : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="longitude">Longitude</Label>
          <Input
            id="longitude"
            placeholder={mapAnchorLocation ? String(mapAnchorLocation.lng) : '80.270721'}
            {...register('longitude')}
            aria-invalid={!!errors.longitude}
          />
          {errors.longitude ? <p className="text-sm text-destructive">{errors.longitude.message}</p> : null}
        </div>

        <div className="space-y-2 lg:col-span-2">
          <Label htmlFor="attendance_device">Attendance device</Label>
          <Controller
            control={control}
            name="attendance_device"
            render={({ field }) => (
              <select
                id="attendance_device"
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={field.value || ''}
                onChange={field.onChange}
              >
                <option value="">No attendance device linked</option>
                {attendanceDevices.map((device) => (
                  <option key={device.id} value={String(device.id)}>
                    {(device.name || device.device_sn)} ({device.device_sn})
                  </option>
                ))}
              </select>
            )}
          />
          <p className="text-xs text-muted-foreground">
            Link the device installed at this location so ADMS punches can find the job and work-day entry.
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button type="submit" className="rounded-lg" disabled={createMutation.isPending || updateMutation.isPending}>
          {selectedLocation ? <Save className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
          {selectedLocation ? 'Save Changes' : 'Add Location'}
        </Button>
        <Button type="button" variant="outline" className="rounded-lg" onClick={handleCancelEdit}>
          <X className="mr-2 h-4 w-4" />
          Cancel
        </Button>
      </div>
    </form>
  )

  if (isLocationBrowseMode) {
    return (
      <LocationsSitesDirectory
        locations={locations}
        isLoading={isLoading}
        errorMessage={error ? getErrorMessage(error) : null}
        workers={(workersLastLocationQuery.data?.results ?? []).map((worker) => ({
          id: worker.id,
          display_name: worker.display_name,
        }))}
        customers={customers.map((customer) => ({
          id: customer.id,
          userId: customer.user_id,
          groupId: customer.user_group,
          label: getUserDropdownLabel(customer, customer.id),
        }))}
        mapsReady={canDisplayMap && mapsAvailable}
        defaultCenter={serviceInstanceLocation}
        isSaving={createMutation.isPending || updateMutation.isPending || deleteMutation.isPending}
        onSave={async (payload, id) => {
          if (id) {
            await updateMutation.mutateAsync({ id, payload })
            return
          }
          await createMutation.mutateAsync(payload)
        }}
        onDelete={async (id) => {
          await deleteMutation.mutateAsync(id)
        }}
      />
    )
  }

  if (!canUseMaps) {
    return (
      <div className="space-y-6 pb-2">
        {returnToPath ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-lg"
            onClick={() => navigate(returnToPath)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to jobs
          </Button>
        ) : null}
        <Card className="rounded-[28px] border-border/80">
          <CardContent className="flex min-h-[260px] items-center justify-center p-6 text-center">
            <div className="space-y-2">
              <p className="text-base font-semibold text-foreground">Map usage is not enabled</p>
              <p className="max-w-md text-sm text-muted-foreground">
                Enable the Map usage feature for this org service to use location search, worker map markers, and job map assignment.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center rounded-[28px] border border-destructive/20 bg-card">
        <div className="text-center">
          <p className="mb-2 text-destructive">Error loading locations</p>
          <p className="text-sm text-muted-foreground">{getErrorMessage(error)}</p>
        </div>
      </div>
    )
  }
  const renderLocationMap = (
    editable = false,
    mapHeight = LOCATION_MAP_HEIGHT,
    containerClassName = 'rounded-2xl',
  ) => (
    <div className={`relative overflow-hidden border border-border/70 ${containerClassName}`}>
      {mapProvider === 'openstreetmap' ? (
        <OpenStreetMapLocationView
          markerPosition={markerPosition}
          workerGroups={isLocationBrowseMode ? [] : workerLocationMarkerGroups}
          permanentWorkerGroups={isLocationBrowseMode ? [] : permanentWorkerLocationMarkerGroups}
          locationMarkers={isLocationBrowseMode
            ? locationMapMarkers.map((location) => ({
                ...location,
                isSelected: selectedMapLocationId === location.id,
              }))
            : []}
          geofencePoints={geofencePoints}
          editable={editable}
          isDrawing={isDrawing}
          workerMapPin={isLocationBrowseMode ? null : workerMapPin}
          getWorkerMapBadge={getWorkerMapBadge}
          lockViewportToMarker={Boolean(selectedJobId || selectedAssignmentId)}
          showJobLocationPulse={showJobLocationPulse}
          jobLocationLabel={jobMarkerTitle}
          jobLocationAddress={selectedJobMarkerInfo?.address}
          viewportRadiusKm={showJobLocationPulse ? effectiveMapRadiusKm : undefined}
          mapHeight={mapHeight}
          onMapPointClick={handleOpenStreetMapClick}
          onLocationMarkerClick={handleMapLocationSelect}
          showPrimaryMarker={!isLocationBrowseMode}
        />
      ) : mapsAvailable ? (
        <>
          <GoogleMap
            mapContainerStyle={{
              width: '100%',
              height: `${mapHeight}px`,
            }}
            center={markerPosition}
            zoom={14}
            onLoad={(map) => setMapRef(map)}
            onClick={editable ? handleMapClick : undefined}
            onDragStart={() => setWorkerMapPin(null)}
            options={{
              mapTypeControl: false,
              streetViewControl: false,
              fullscreenControl: false,
              zoomControl: false,
              rotateControl: false,
              scaleControl: false,
            }}
          >
            {!showJobLocationPulse && !isLocationBrowseMode ? <Marker key="default-location" position={markerPosition} /> : null}
            {isLocationBrowseMode
              ? locationMapMarkers.map((location) => (
                <Marker
                  key={`location-marker-${location.id}`}
                  position={location.position}
                  title={`${location.place} (${location.linkedJobCount} jobs)`}
                  onClick={() => handleMapLocationSelect(location.id)}
                  icon={{
                    path: window.google.maps.SymbolPath.CIRCLE,
                    fillColor: selectedMapLocationId === location.id ? location.darkColorHex : location.colorHex,
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeOpacity: 1,
                    strokeWeight: 2,
                    scale: selectedMapLocationId === location.id ? 8 : 6,
                  }}
                />
              ))
              : null}
            {showJobLocationPulse ? (
              <OverlayView
                position={mapAnchorLocation ?? markerPosition}
                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                getPixelPositionOffset={() => ({
                  x: 0,
                  y: 0,
                })}
              >
                <div className="-translate-x-1/2 -translate-y-full">
                  <JobLocationPulseMarker label={jobLocationLabel} />
                </div>
              </OverlayView>
            ) : null}
            {!isLocationBrowseMode ? permanentWorkerLocationMarkerGroups.map(({ workers, position }) => (
              <Marker
                key={`permanent-workers-${position.lat}-${position.lng}`}
                position={position}
                title={workers.map((worker) => worker.display_name || `Worker ${worker.id}`).join(', ')}
                zIndex={998}
                icon={{
                  path: WORKER_ICON_PATH,
                  fillColor: getWorkerMapBadge(workers[0])?.colorHex || '#c026d3',
                  fillOpacity: 1,
                  strokeColor: '#ffffff',
                  strokeOpacity: 1,
                  strokeWeight: 1.4,
                  scale: 2.7,
                  anchor: new window.google.maps.Point(12, 21),
                  labelOrigin: new window.google.maps.Point(12, -3),
                }}
                label={{
                  text: workers.length === 1
                    ? String(getWorkerMapBadge(workers[0])?.number ?? 1)
                    : `${getWorkerMapBadge(workers[0])?.number ?? workers.length}+${workers.length - 1}`,
                  color: getWorkerMapBadge(workers[0])?.colorHex || '#c026d3',
                  fontSize: '18px',
                  fontWeight: '800',
                }}
              />
            )) : null}
            {!isLocationBrowseMode ? workerLocationMarkerGroups.map(({ workers, position }) => {
              const markerColor = getWorkerMapBadge(workers[0])?.colorHex || '#059669'

              return (
                <Marker
                  key={`workers-${position.lat}-${position.lng}`}
                  position={position}
                  title={workers.map((worker) => worker.display_name || `Worker ${worker.id}`).join(', ')}
                  zIndex={999}
                  icon={{
                    path: WORKER_ICON_PATH,
                    fillColor: markerColor,
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeOpacity: 1,
                    strokeWeight: 1.4,
                    scale: 2.7,
                    anchor: new window.google.maps.Point(12, 21),
                    labelOrigin: new window.google.maps.Point(12, -3),
                  }}
                  label={{
                    text: workers.length === 1
                      ? String(getWorkerMapBadge(workers[0])?.number ?? 1)
                      : `${getWorkerMapBadge(workers[0])?.number ?? workers.length}+${workers.length - 1}`,
                    color: markerColor,
                    fontSize: '18px',
                    fontWeight: '800',
                  }}
                />
              )
            }) : null}

            {/* POLYGON */}
            {geofencePoints.length >= 3 && (
              <Polygon
                paths={geofencePoints.map(([lat, lng]) => ({
                  lat,
                  lng,
                }))}
                options={{
                  fillOpacity: 0.2,
                  strokeWeight: 2,
                  editable,
                }}
              />
            )}
          </GoogleMap>
          {workerMapPin ? (
            <div className="pointer-events-none absolute left-1/2 top-1/2 z-20 flex -translate-x-1/2 -translate-y-full flex-col items-center">
              <div className={`flex items-center gap-2 rounded-full border-4 border-white px-4 py-2 text-sm font-bold text-white shadow-2xl ${workerMapPin.bgClass || (workerMapPin.isOnline ? 'bg-emerald-600' : 'bg-slate-500')}`}>
                <span className="relative flex h-11 w-11 items-center justify-center rounded-full bg-white">
                  <ServiceWorkerMarkerGlyph color={workerMapPin.colorHex || (workerMapPin.isOnline ? '#059669' : '#64748b')} />
                </span>
                <span className={`flex h-8 min-w-8 items-center justify-center rounded-full px-2 text-base text-white ${workerMapPin.darkBgClass || (workerMapPin.isOnline ? 'bg-emerald-800' : 'bg-slate-700')}`}>
                  {workerMapPin.count}
                </span>
                <span>{workerMapPin.label}</span>
              </div>
              <div className={`h-0 w-0 border-x-[12px] border-t-[18px] border-x-transparent drop-shadow ${workerMapPin.tailBorderClass || (workerMapPin.isOnline ? 'border-t-emerald-600' : 'border-t-slate-500')}`} />
            </div>
          ) : null}
        </>
      ) : (
        <div className="flex h-[450px] items-center justify-center bg-muted/20 px-6 text-center">
          <div className="space-y-2">
            <p className="text-sm font-medium text-foreground">Map unavailable</p>
            <p className="text-sm text-muted-foreground">
              {mapsStatusMessage || (isLoaded ? 'Unable to display the map.' : 'Loading Google Maps...')}
            </p>
          </div>
        </div>
      )}
    </div>
  )

  if (isRecurringWorkerAssignmentPage && selectedJobId && !isLiveMode) {
    const activeJob = selectedJobQuery.data
    const backPath = returnToPath || withServiceInstanceId('/job/recurring', serviceInstanceId)
    const customerLabel = activeJob?.client_name || activeJob?.client_user_info?.name || 'Customer'
    const customerInitials = customerLabel
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() || '')
      .join('') || 'CU'

    return (
      <div className="space-y-4 pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-[#193229]">Assign Workers to Recurring Jobs</h1>
            <p className="mt-1 text-sm text-[#708078]">Match available workers to the recurring schedule.</p>
          </div>
          <Button variant="outline" size="sm" className="h-9 rounded-[8px] border-[#d0e0ff] bg-[#eef4ff] text-[#2f6fdc] hover:bg-[#e4eeff]" asChild>
            <Link to={backPath}>
              <ArrowLeft className="mr-2 h-3.5 w-3.5" />
              Back to Jobs
            </Link>
          </Button>
        </div>

        <Card className="rounded-xl border border-[#cfe0ff] bg-[#edf3ff] shadow-none">
          <CardContent className="flex items-center gap-3 px-4 py-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#2f6fdc] text-sm font-semibold text-white">{customerInitials}</div>
            <div className="min-w-0">
              <p className="truncate text-base font-semibold text-[#193229]">{customerLabel}</p>
              <p className="truncate text-xs text-[#708078]">
                {(activeJob?.title || 'Recurring service')} · {(activeJob?.location_info?.place || activeJob?.location || activeJob?.address || 'Location not set')}
              </p>
              <p className="truncate text-xs font-medium text-[#2f6fdc]">{formatSelectedJobSchedule(activeJob) || 'Schedule not set'}</p>
            </div>
          </CardContent>
        </Card>

        <div className="border-b border-border/80">
          <div className="flex items-center gap-6 px-1">
            {[
              { key: 'assign', label: 'Assign Workers' },
              { key: 'schedule', label: 'Schedule View' },
              { key: 'conflicts', label: 'Conflicts' },
              { key: 'history', label: 'History' },
            ].map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={cn(
                  'h-10 border-b-2 text-sm transition-colors',
                  assignmentViewTab === tab.key
                    ? 'border-[#15975d] font-semibold text-[#087744]'
                    : 'border-transparent text-[#708078] hover:text-foreground',
                )}
                onClick={() => setAssignmentViewTab(tab.key as 'assign' | 'schedule' | 'conflicts' | 'history')}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {assignmentViewTab !== 'assign' ? (
          <Card className="rounded-xl border-border/80">
            <CardContent className="px-4 py-8 text-center text-sm text-muted-foreground">
              {assignmentViewTab === 'schedule' ? 'Schedule view is coming next.' : null}
              {assignmentViewTab === 'conflicts' ? 'Conflict review is coming next.' : null}
              {assignmentViewTab === 'history' ? 'Assignment history is coming next.' : null}
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="rounded-xl border-border/80">
              <CardHeader className="pb-2">
                <CardTitle className="text-xl">Assignment Method</CardTitle>
                <p className="text-sm text-muted-foreground">Choose how workers should be allocated across this recurring job.</p>
              </CardHeader>
              <CardContent>
                <div className="grid gap-2 md:grid-cols-3">
                  {[
                    { key: 'same_worker', title: 'Assign the same worker for all visits' },
                    { key: 'rotate', title: 'Rotate workers automatically' },
                    { key: 'per_visit', title: 'Assign per visit' },
                  ].map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => setAssignmentMethod(option.key as 'same_worker' | 'rotate' | 'per_visit')}
                      className={cn(
                        'rounded-lg border px-3 py-4 text-left transition',
                        assignmentMethod === option.key
                          ? 'border-[#15975d] bg-[#e7f7ee]'
                          : 'border-border/80 bg-background hover:border-[#9ad3b4]',
                      )}
                    >
                      <div className={cn('mb-2 h-3.5 w-3.5 rounded-full border', assignmentMethod === option.key ? 'border-[#15975d] bg-[#15975d]' : 'border-muted-foreground/40')} />
                      <p className="text-sm font-medium text-foreground">{option.title}</p>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl border-border/80">
              <CardHeader className="space-y-2 pb-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <CardTitle className="text-xl">Select Workers</CardTitle>
                    <p className="text-sm text-muted-foreground">{assignmentWorkerRows.length} workers available to assign. Skills are used when they match this job category.</p>
                  </div>
                  <Button type="button" variant="outline" className="h-9 rounded-[8px] border-[#d0e0ff] bg-[#eef4ff] text-[#2f6fdc] hover:bg-[#e4eeff]" onClick={handleAutoSelectAssignmentWorkers}>
                    Auto-assign
                  </Button>
                </div>
                <div className="grid gap-2 md:grid-cols-3">
                  <div className="relative md:col-span-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={assignmentWorkerSearch}
                      onChange={(event) => setAssignmentWorkerSearch(event.target.value)}
                      placeholder="Search workers..."
                      className="h-10 pl-9"
                    />
                  </div>
                  <Select value={assignmentRoleFilter} onValueChange={setAssignmentRoleFilter}>
                    <SelectTrigger className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {assignmentRoleFilterOptions.map((option) => (
                        <SelectItem key={option} value={option}>{option === 'all' ? 'All skills' : option}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={assignmentAvailabilityFilter} onValueChange={(value) => setAssignmentAvailabilityFilter(value as 'all' | 'available' | 'limited')}>
                    <SelectTrigger className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All availability</SelectItem>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="limited">Limited</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-hidden rounded-xl border border-border/80">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10" />
                        <TableHead>Worker</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Workload</TableHead>
                        <TableHead>Availability</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assignmentWorkerRows.map((row) => {
                        const isSelected = selectedAssignmentWorkerIds.includes(row.id)
                        const isAlreadyLinked = Boolean(row.assignment)
                        return (
                          <TableRow key={`assign-row-${row.id}`} className={isSelected ? 'bg-[#e8f7ef]' : ''}>
                            <TableCell>
                              <Checkbox
                                checked={isSelected}
                                disabled={isAlreadyLinked}
                                onCheckedChange={() => {
                                  if (isAlreadyLinked) {
                                    return
                                  }
                                  toggleAssignmentWorkerSelection(row.id)
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Avatar className="h-8 w-8">
                                  <AvatarFallback className="text-xs">{getWorkerInitials(row.name)}</AvatarFallback>
                                </Avatar>
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-semibold">{row.name}</p>
                                  <p className="truncate text-xs text-muted-foreground">{row.distanceLabel}</p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="rounded-full border-border/70 bg-background text-xs">{row.roleLabel}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{row.workloadLabel}</TableCell>
                            <TableCell>
                              <div className="flex min-w-[180px] items-center gap-2">
                                <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#e8eef0]">
                                  <div
                                    className={cn('h-full rounded-full', row.availabilityPercent >= 75 ? 'bg-[#15975d]' : 'bg-[#f59e0b]')}
                                    style={{ width: `${row.availabilityPercent}%` }}
                                  />
                                </div>
                                <span className={cn('w-10 text-right text-xs font-semibold', row.availabilityPercent >= 75 ? 'text-[#15975d]' : 'text-[#f59e0b]')}>
                                  {row.availabilityPercent}%
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                      {assignmentWorkerRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                            No workers match the current filters.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-xl border-border/80">
              <CardHeader className="pb-2">
                <CardTitle className="text-xl">Assignment Preview</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap items-center justify-between gap-3">
                {selectedAssignmentWorkerRows.length > 0 ? (
                  <div className="min-w-0 space-y-1.5">
                    <p className="text-sm font-semibold text-foreground">
                      {selectedAssignmentWorkerRows.length} worker{selectedAssignmentWorkerRows.length === 1 ? '' : 's'} selected
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {selectedAssignmentWorkerRows.slice(0, 3).map((row) => row.name).join(', ')}
                      {selectedAssignmentWorkerRows.length > 3 ? ` +${selectedAssignmentWorkerRows.length - 3} more` : ''}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Assigned to {Math.max(1, Number(activeJob?.recurrence_count ?? 156))} yearly visits
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">Select workers to preview assignment.</p>
                )}
                <Badge variant="outline" className="rounded-full border-emerald-300 bg-emerald-50 px-3 py-1 text-emerald-700">
                  No conflicts
                </Badge>
              </CardContent>
            </Card>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button type="button" variant="outline" className="h-10 rounded-[8px] px-5" onClick={() => navigate(backPath)}>
                Cancel
              </Button>
              <div className="flex items-center gap-2">
                <Button type="button" variant="outline" className="h-10 rounded-[8px] px-5" onClick={() => navigate(backPath)}>
                  Back
                </Button>
                <Button
                  type="button"
                  className="h-10 rounded-[8px] border border-[#15975d] bg-[#15975d] px-5 text-[11px] font-semibold text-white hover:bg-[#118350]"
                  disabled={assignmentCreateMutation.isPending}
                  onClick={() => {
                    void handleSaveAssignmentWorkers()
                  }}
                >
                  <Save className="mr-2 h-3.5 w-3.5" />
                  Save Assignment
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    )
  }

  return (
    <div className={isLiveMode ? 'space-y-3 pb-2' : 'space-y-6 pb-2'}>

      {returnToPath ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-lg"
          onClick={() => navigate(returnToPath)}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to jobs
        </Button>
      ) : null}

      {isWorkerAssignmentPage && selectedJobId && !isLiveMode ? (
        <Card className="rounded-[28px] border-blue-200 bg-blue-50/40 border-border/80">
          <CardContent className="space-y-3 px-4 py-3 text-sm">
            <p className="font-medium text-foreground">How assignment works</p>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>Click <span className="font-medium text-foreground">Add</span> next to a worker to create a new assignment, or fill an open crew/planner slot when one matches their skill.</li>
              <li className="flex flex-wrap items-center gap-x-1 gap-y-1">
                <span>If a worker has skills, they should match this job&apos;s category. Workers without skills can still be assigned{selectedJobQuery.data?.category_name ? '' : '.'}</span>
                {selectedJobQuery.data?.category_name ? (
                  <>
                    <JobCategoryBadge job={selectedJobQuery.data} />
                    <span>.</span>
                  </>
                ) : null}
              </li>
              <li>Workers without a map location still appear in the list and can be assigned.</li>
            </ul>
            {manualAssignmentCreatePath ? (
              <Button variant="outline" size="sm" className="rounded-lg bg-background" asChild>
                <Link to={manualAssignmentCreatePath}>
                  Create assignment manually
                </Link>
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {selectedJobId && !isLiveMode ? (
        <Card className="rounded-[28px] border-border/80">
          <CardContent className="px-4 py-3">
            {jobAssignmentsQuery.isLoading ? (
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: 3 }).map((_, index) => (
                  <Skeleton key={index} className="h-8 w-36 rounded-full" />
                ))}
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="outline" className="rounded-md border-border/80 bg-muted/30 px-2.5 py-1 text-xs font-semibold">
                  {assignmentResourceTotals.requiredCount} required
                </Badge>
                <Badge variant="outline" className="rounded-md border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                  {assignmentResourceTotals.assignedCount} assigned
                </Badge>
                {assignmentResourceTotals.unassignedCount > 0 ? (
                  <Badge variant="outline" className="rounded-md border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800">
                    {assignmentResourceTotals.unassignedCount} open
                  </Badge>
                ) : null}
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      <section className={isLiveMode
        ? (selectedJobId
            ? 'grid gap-3 xl:grid-cols-[minmax(0,1.2fr)_minmax(300px,0.8fr)_minmax(320px,0.9fr)]'
            : 'grid gap-3 xl:grid-cols-[minmax(0,1.55fr)_minmax(320px,0.9fr)]')
        : 'grid gap-6 xl:grid-cols-[minmax(0,1.45fr)_minmax(320px,0.85fr)]'}>
        <Card className="rounded-[28px] border-border/80">
          <CardHeader className={isLiveMode ? 'pb-2' : 'pb-3'}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-[280px] flex-1 space-y-2">
                {!isLiveMode ? (
                  <Badge variant="outline" className="w-fit rounded-md border-emerald-200 bg-emerald-50 text-emerald-700">
                    Geofence workspace
                  </Badge>
                ) : null}
                {isLiveMode ? (
                  <div className="flex items-center gap-2 overflow-x-auto pb-1">
                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/12 text-primary">
                      <CalendarDays className="h-4 w-4" />
                    </span>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 shrink-0 rounded-full"
                      aria-label="Previous 7 days"
                      onClick={() => shiftLiveDayWindow(-1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="flex min-w-max items-center gap-2">
                      {liveDayOptions.map((day) => {
                        const isSelected = day.key === liveSelectedDayKey
                        const dayLabel = day.date.toLocaleDateString(undefined, { weekday: 'short' })
                        const dayNumber = day.date.toLocaleDateString(undefined, { day: '2-digit' })
                        return (
                          <Button
                            key={day.key}
                            type="button"
                            variant="ghost"
                            size="sm"
                            className={`h-[62px] w-[62px] shrink-0 rounded-2xl border px-2 py-1.5 transition ${isSelected
                              ? 'border-primary bg-primary text-primary-foreground hover:bg-primary'
                              : 'border-border/70 bg-background text-foreground hover:border-primary/40 hover:bg-primary/5'}`}
                            onClick={() => selectLiveDay(day.key)}
                          >
                            <span className="flex flex-col items-center justify-center leading-none">
                              <span className={`text-[9px] font-semibold uppercase tracking-[0.08em] ${isSelected ? 'text-primary-foreground/90' : 'text-muted-foreground'}`}>
                                {dayLabel}
                              </span>
                              <span className={`mt-1 text-xl font-bold ${isSelected ? 'text-primary-foreground' : 'text-foreground'}`}>
                                {dayNumber}
                              </span>
                            </span>
                          </Button>
                        )
                      })}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 shrink-0 rounded-full"
                      aria-label="Next 7 days"
                      onClick={() => shiftLiveDayWindow(1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 shrink-0 rounded-full px-3 text-xs font-semibold"
                      onClick={jumpToLiveCurrentWeek}
                    >
                      This week
                    </Button>
                    <Badge variant="outline" className="ml-auto shrink-0 rounded-full border-border/70 bg-muted/30 px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground">
                      {liveJobsQuery.isFetching ? 'Updating...' : `${liveFilteredJobs.length} jobs`}
                    </Badge>
                  </div>
                ) : null}
                {selectedJobId && !isLiveMode ? (
                  <div className="rounded-2xl border border-border/70 bg-muted/30 p-3 text-sm">
                    <p className="text-xs font-medium text-muted-foreground">{isLiveMode ? 'Tracking job' : 'Assigning workers for'}</p>
                    {selectedJobQuery.isLoading ? (
                      <Skeleton className="mt-2 h-4 w-56" />
                    ) : selectedJobQuery.data ? (
                      <div className="mt-1 space-y-1.5">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="flex min-w-0 flex-wrap items-center gap-2">
                            <JobTitleLink
                              jobId={selectedJobQuery.data.id}
                              title={selectedJobQuery.data.title}
                              jobRef={selectedJobQuery.data.job_ref}
                              serviceInstanceId={serviceInstanceId}
                              className="font-semibold"
                            />
                            <Badge variant="outline">{getJobStatusLabel(selectedJobQuery.data.workflow_status_code ?? selectedJobQuery.data.status)}</Badge>
                            <JobCategoryBadge job={selectedJobQuery.data} />
                          </div>
                          {selectedAssignmentId ? (
                            <p className="text-sm font-semibold text-primary">
                              {selectedAssignmentQuery.isLoading || selectedJobQuery.isLoading
                                ? 'Loading...'
                                : ([selectedAssignmentSkillLabel, selectedAssignmentCategoryLabel].filter(Boolean).join(' · ') || '-')}
                            </p>
                          ) : null}
                        </div>
                        <p className="text-xs font-medium text-muted-foreground">
                          {formatSelectedJobSchedule(selectedJobQuery.data)}
                        </p>
                      </div>
                    ) : (
                      <p className="mt-1 text-destructive">Job not found.</p>
                    )}
                  </div>
                ) : null}
                {!isLiveMode && selectedJobId ? (
                  <div className="space-y-2">
                    <Label htmlFor="job-location-id">Job location</Label>
                    <Input
                      id="job-location-id"
                      readOnly
                      value={
                        selectedJobQuery.isLoading
                          ? 'Loading job...'
                          : selectedJobQuery.data
                            ? resolveJobLocationLabel(selectedJobQuery.data)
                            : jobLocationLabel
                      }
                      className="bg-muted/40 font-semibold text-foreground"
                    />
                  </div>
                ) : !isLiveMode && mapProvider === 'openstreetmap' ? (
                  <div className="relative space-y-2">
                    <Label htmlFor="location-place-search">Find a place</Label>
                    <Input
                      id="location-place-search"
                      placeholder="Search for a place or address"
                      value={placeSearchValue}
                      onChange={(event) => setPlaceSearchValue(event.target.value)}
                      onFocus={() => setIsPlaceSearchFocused(true)}
                      onBlur={handlePlaceSearchBlur}
                    />
                    {placeSearchOptions.length > 0 ? (
                      <div className="absolute z-30 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-border/70 bg-popover p-1 shadow-lg">
                        {placeSearchOptions.map((option) => (
                          <button
                            key={option.id}
                            type="button"
                            className="w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-muted"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => selectPlaceSearchOption(option)}
                          >
                            <span className="block font-medium text-foreground">{option.label}</span>
                            {option.details ? (
                              <span className="mt-0.5 block text-xs text-muted-foreground">{option.details}</span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                    <p className="text-xs text-muted-foreground">
                      {isSearchingPlace
                        ? 'Searching places...'
                        : 'Default is the service instance coordinate. Change it by searching, or clear it to keep the service instance location.'}
                    </p>
                  </div>
                ) : !isLiveMode && mapsAvailable ? (
                  <div className="space-y-2">
                    <Label htmlFor="location-place-search">Find a place</Label>
                    <StandaloneSearchBox
                      onLoad={(ref) => setSearchBox(ref)}
                      onPlacesChanged={handlePlaceSearchChanged}
                    >
                      <Input
                        id="location-place-search"
                        placeholder="Search for a place or address"
                        value={placeSearchValue}
                        onChange={(event) => setPlaceSearchValue(event.target.value)}
                        onBlur={handlePlaceSearchBlur}
                      />
                    </StandaloneSearchBox>
                    <p className="text-xs text-muted-foreground">
                      Default is the service instance coordinate. Change it by searching, or clear it to keep the service instance location.
                    </p>
                  </div>
                ) : !isLiveMode ? (
                  <div className="space-y-2">
                    <Label htmlFor="location-place-search">Find a place</Label>
                    <Input
                      id="location-place-search"
                      placeholder="Search unavailable until Google Maps loads"
                      disabled
                    />
                    <p className="text-xs text-muted-foreground">
                      {mapsStatusMessage || 'Loading Google Maps search...'}
                    </p>
                  </div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs whitespace-nowrap text-blue-700">
                  <span>
                    {isLocationBrowseMode
                      ? `${locationMapMarkers.length} locations`
                      : `${visibleWorkerLocationMarkers.length}/${statusFilteredWorkerLocationMarkers.length}${workersLastLocationQuery.isFetching ? ' …' : ''}`}
                  </span>
                </div>
                {selectedJobId || selectedAssignmentId ? (
                  isLiveMode ? (
                    <Badge variant="outline" className="h-9 shrink-0 rounded-md border-border/70 px-2.5 text-xs font-semibold">
                      {DEFAULT_JOB_LOCATION_VIEW_RADIUS_KM} km
                    </Badge>
                  ) : (
                    <Select
                      value={String(jobLocationViewRadiusKm)}
                      onValueChange={(value) => setJobLocationViewRadiusKm(Number(value))}
                    >
                      <SelectTrigger
                        size="sm"
                        className="h-9 w-[5.25rem] shrink-0"
                        aria-label="Map view radius"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {JOB_LOCATION_VIEW_RADIUS_OPTIONS_KM.map((radiusKm) => (
                          <SelectItem key={radiusKm} value={String(radiusKm)}>
                            {formatJobLocationViewRadiusOption(radiusKm, distanceUnitSystem)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label={
                    isLocationBrowseMode
                      ? 'Show all locations'
                      : selectedJobId || selectedAssignmentId
                      ? 'Focus job location'
                      : 'Show all workers'
                  }
                  title={
                    isLocationBrowseMode
                      ? 'Show all locations'
                      : selectedJobId || selectedAssignmentId
                      ? 'Focus job location'
                      : 'Show all workers'
                  }
                  onClick={isLocationBrowseMode
                    ? () => {
                        setSelectedMapLocationId(null)
                        fitMapToLocationMarkers()
                      }
                    : (selectedJobId || selectedAssignmentId ? fitMapToJobLocation : fitMapToWorkerMarkers)}
                  disabled={isLocationBrowseMode
                    ? !canDisplayMap || locationMapMarkers.length === 0
                    : (selectedJobId || selectedAssignmentId
                        ? !canDisplayMap || !mapAnchorLocation
                        : !canDisplayMap || statusFilteredWorkerLocationMarkers.length === 0)}
                >
                  {isLocationBrowseMode ? (
                    <MapPin className="h-4 w-4" />
                  ) : selectedJobId || selectedAssignmentId ? (
                    <Crosshair className="h-4 w-4" />
                  ) : (
                    <Users className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className={isLiveMode ? 'space-y-2 pt-0' : 'space-y-3'}>
            {renderLocationMap(
              !isLiveMode,
              isLiveMode ? (selectedJobId ? 420 : 500) : LOCATION_MAP_HEIGHT,
              isLiveMode ? 'rounded-lg' : 'rounded-2xl',
            )}
            {!isLiveMode && isEditMode ? (
              <>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setGeofencePoints((current) => current.slice(0, Math.max(0, current.length - 1)))}
                    disabled={geofencePoints.length === 0}
                  >
                    Undo point
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setGeofencePoints([])}
                    disabled={geofencePoints.length === 0}
                  >
                    Clear geofence
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsDrawing(true)}
                    disabled={!canDisplayMap}
                  >
                    Add points on map
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsDrawing(false)}
                  >
                    Stop Drawing
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  {isDrawing
                    ? `Click the map to add points. A geofence requires at least 3 points and supports up to ${MAX_GEOFENCE_POINTS} points.`
                    : `A geofence polygon requires at least 3 points and supports up to ${MAX_GEOFENCE_POINTS} points.`}
                </p>
              </>
            ) : null}
          </CardContent>
        </Card>

        {isLiveMode ? (
          <>
            <Card className="rounded-[24px] border-border/80">
              <CardContent className="space-y-2.5 px-3 pt-3">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={liveJobSearch}
                    onChange={(event) => setLiveJobSearch(event.target.value)}
                    placeholder="Search by title, ref, customer"
                    className="pl-9"
                  />
                </div>
                {liveJobsQuery.isLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <div key={index} className="rounded-xl border border-border/70 p-3">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="mt-2 h-3 w-32" />
                      </div>
                    ))}
                  </div>
                ) : liveJobsQuery.isError ? (
                  <p className="text-sm text-destructive">{getErrorMessage(liveJobsQuery.error)}</p>
                ) : liveFilteredJobs.length === 0 ? (
                  <p className="rounded-xl border border-border/70 p-3 text-sm text-muted-foreground">
                    No live jobs found.
                  </p>
                ) : (
                  <div className={`space-y-1.5 overflow-y-auto pr-1 ${selectedJobId ? 'max-h-[520px]' : 'max-h-[620px]'}`}>
                    {liveFilteredJobs.map((job) => {
                      const isSelected = String(job.id) === String(selectedJobId)
                      const statusLabel = getJobStatusLabel(job.workflow_status_code ?? job.status)
                      const scheduleLabel = formatSelectedJobSchedule(job) || 'Schedule not set'
                      const locationLabel = resolveJobLocationLabel(job)
                      const priorityLabel = formatPriorityLabel(job.priority)
                      const isSelectedLiveJob = String(job.id) === String(selectedJobId)
                      const primaryWorkerLabel = getLivePrimaryWorkerLabel(
                        job,
                        isSelectedLiveJob ? assignmentResourceTotals.assignedCount : undefined,
                      )
                      const attendanceCounts = getLiveAttendanceCounts(job)
                      const displayCounts = isSelectedLiveJob
                        ? {
                            checkedIn: liveAssignedWorkerCounts.onsite,
                            late: liveAssignedWorkerCounts.late,
                            checkedOut: liveAssignedWorkerCounts.departed,
                            notShown: liveAssignedWorkerCounts.noShow,
                          }
                        : attendanceCounts
                      return (
                        <button
                          key={`live-job-${job.id}`}
                          type="button"
                          onClick={() => selectLiveJob(String(job.id))}
                          className={`w-full rounded-xl border p-2.5 text-left transition ${isSelected ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100 shadow-[0_8px_20px_-18px_rgba(29,78,216,0.8)]' : 'border-border/70 bg-background hover:border-blue-200 hover:bg-blue-50/40'}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0 space-y-1">
                              <p className="line-clamp-2 text-[13px] font-semibold text-foreground">{job.title}</p>
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                              <span className="text-[9px] font-semibold uppercase tracking-[0.08em] text-blue-700">{statusLabel}</span>
                            </div>
                          </div>

                          <div className="mt-1.5 flex items-center gap-1.5 overflow-x-auto whitespace-nowrap pb-0.5">
                            <Badge variant="outline" className="border-border/70 bg-muted/30 text-[10px] text-muted-foreground">
                              {job.job_ref || `Job #${job.id}`}
                            </Badge>
                            <Badge variant="outline" className={`text-[10px] ${getPriorityBadgeClassName(job.priority)}`}>
                              {priorityLabel}
                            </Badge>
                            {job.category_name ? (
                              <JobCategoryBadge job={job} />
                            ) : (
                              <Badge variant="outline" className="border-violet-200 bg-violet-50 text-[10px] text-violet-700">
                                Uncategorized
                              </Badge>
                            )}
                          </div>

                          <div className="mt-1.5 space-y-1.5">
                            <div className="flex items-start gap-2 text-xs text-muted-foreground">
                              <CalendarDays className="mt-0.5 h-3.5 w-3.5 text-blue-600" />
                              <span className="line-clamp-2">{scheduleLabel}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <MapPin className="h-3.5 w-3.5 text-emerald-600" />
                              <span className="line-clamp-1">{locationLabel}</span>
                            </div>
                          </div>

                          <div className="mt-1.5 flex items-center justify-between gap-3 border-t border-border/60 pt-1.5 text-[11px] text-muted-foreground">
                            <span className="inline-flex min-w-0 items-center gap-1 text-indigo-700">
                              <UserRound className="h-3.5 w-3.5" />
                              <span className="line-clamp-1">{primaryWorkerLabel}</span>
                            </span>
                            <div className="flex shrink-0 items-center gap-3">
                              <span className="inline-flex items-center gap-1 text-emerald-700">
                                <LogIn className="h-3.5 w-3.5" />
                                {displayCounts.checkedIn}
                              </span>
                              <span className="inline-flex items-center gap-1 text-rose-700">
                                <AlarmClock className="h-3.5 w-3.5" />
                                {displayCounts.late}
                              </span>
                              <span className="inline-flex items-center gap-1 text-slate-700">
                                <LogOut className="h-3.5 w-3.5" />
                                {displayCounts.checkedOut}
                              </span>
                              <span className="inline-flex items-center gap-1 text-amber-700">
                                <UserX className="h-3.5 w-3.5" />
                                {displayCounts.notShown}
                              </span>
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {selectedJobId ? (
            <Card className="rounded-[24px] border-border/80">
              <CardContent className="space-y-2.5 px-3 pt-3">
                {selectedJobId && selectedJobQuery.isLoading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ) : selectedLiveJob ? (
                  <div className="space-y-2.5 rounded-xl border border-border/70 bg-muted/20 p-2.5">
                    <p className="text-[15px] font-semibold text-foreground">{selectedLiveJob.title}</p>
                    <div className="flex items-center gap-1.5 overflow-x-auto whitespace-nowrap pb-0.5">
                      <Badge variant="outline" className="border-border/70 bg-muted/30 text-[10px] text-muted-foreground">
                        {selectedLiveJob.job_ref || `Job #${selectedLiveJob.id}`}
                      </Badge>
                      <Badge variant="outline" className={`text-[10px] ${getPriorityBadgeClassName(selectedLiveJob.priority)}`}>
                        {formatPriorityLabel(selectedLiveJob.priority)}
                      </Badge>
                      <Badge variant="outline" className="border-blue-200 bg-white text-[10px] text-blue-700">{getJobStatusLabel(selectedLiveJob.workflow_status_code ?? selectedLiveJob.status)}</Badge>
                      <JobCategoryBadge job={selectedLiveJob} />
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-start gap-2 text-muted-foreground">
                        <CalendarDays className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-600" />
                        <span className="text-foreground">{formatSelectedJobSchedule(selectedLiveJob) || 'Schedule not set'}</span>
                      </div>
                      <div className="flex items-start gap-2 text-muted-foreground">
                        <UserRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-indigo-600" />
                        <span className="text-foreground">{selectedLiveJob.client_name || selectedLiveJob.client_user_info?.name || '-'}</span>
                      </div>
                      <div className="flex items-start gap-2 text-muted-foreground">
                        <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                        <span className="text-foreground">{resolveJobLocationLabel(selectedLiveJob)}</span>
                      </div>
                      <div className="flex items-start gap-2 text-muted-foreground">
                        <Users className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-600" />
                        <span className="text-foreground">{getLivePrimaryWorkerLabel(selectedLiveJob, assignmentResourceTotals.assignedCount)}</span>
                      </div>

                      <div className="border-t border-border/60 pt-2">
                        <div className="flex flex-wrap items-center gap-1 text-[10px]">
                        <span className="rounded-full bg-emerald-50 px-1.5 py-0.5 text-emerald-700">Onsite {liveAssignedWorkerCounts.onsite}</span>
                        <span className="rounded-full bg-rose-50 px-1.5 py-0.5 text-rose-700">Late {liveAssignedWorkerCounts.late}</span>
                        <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-slate-700">Departed {liveAssignedWorkerCounts.departed}</span>
                        <span className="rounded-full bg-amber-50 px-1.5 py-0.5 text-amber-700">No show {liveAssignedWorkerCounts.noShow}</span>
                        </div>

                        {liveAssignedWorkers.length > 0 ? (
                          <div className="mt-2 max-h-[180px] space-y-1.5 overflow-y-auto pr-1">
                            {liveAssignedWorkers.map((worker) => (
                              <div key={`live-assigned-worker-${worker.id}`} className="flex items-center justify-between gap-2 py-1">
                                <div className="min-w-0">
                                  <p className="truncate text-sm font-medium text-foreground">{worker.name}</p>
                                  {worker.roleLabel ? (
                                    <p className="truncate text-[11px] text-muted-foreground">{worker.roleLabel}</p>
                                  ) : null}
                                </div>
                                <Badge
                                  variant="outline"
                                  className={worker.status === 'Onsite'
                                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                    : worker.status === 'Late'
                                      ? 'border-rose-200 bg-rose-50 text-rose-700'
                                    : worker.status === 'Departed'
                                      ? 'border-slate-300 bg-slate-100 text-slate-700'
                                      : 'border-amber-200 bg-amber-50 text-amber-700'}
                                >
                                  {worker.status}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="mt-2 text-xs text-muted-foreground">No workers assigned to this job yet.</p>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button size="sm" variant="outline" asChild>
                        <Link to={withServiceInstanceId(`/job/${selectedLiveJob.id}`, serviceInstanceId)}>Open job</Link>
                      </Button>
                      <Button size="sm" variant="outline" asChild>
                        <Link to={withServiceInstanceId(`/job/locations?job_id=${encodeURIComponent(String(selectedLiveJob.id))}`, serviceInstanceId)}>View on map</Link>
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => selectLiveJob(null)}>
                        Clear
                      </Button>
                    </div>
                  </div>
                ) : (
                  <p className="rounded-xl border border-dashed border-border/70 p-4 text-sm text-muted-foreground">
                    Select a job from the middle panel to view details.
                  </p>
                )}
              </CardContent>
            </Card>
            ) : null}
          </>
        ) : isLocationBrowseMode ? (
        <Card className="rounded-[28px] border-border/80">
          <CardHeader className="px-4 pb-2 pt-4">
            <CardTitle className="text-lg">Location Jobs</CardTitle>
            <p className="text-sm text-muted-foreground">
              Click a location marker on the map to view jobs for that location.
            </p>
          </CardHeader>
          <CardContent className="space-y-3 px-4 pt-0">
            {!selectedMapLocation ? (
              <div className="rounded-xl border border-dashed border-border/70 p-4 text-sm text-muted-foreground">
                Select a location on the map to load jobs.
              </div>
            ) : selectedMapLocationJobsQuery.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 4 }).map((_, index) => (
                  <div key={index} className="rounded-xl border border-border/70 p-3">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="mt-2 h-3 w-36" />
                  </div>
                ))}
              </div>
            ) : selectedMapLocationJobsQuery.isError ? (
              <p className="text-sm text-destructive">{getErrorMessage(selectedMapLocationJobsQuery.error)}</p>
            ) : (
              <>
                <div className="rounded-xl border border-border/70 bg-muted/20 px-3 py-2">
                  <p className="text-sm font-semibold text-foreground">{selectedMapLocation.place}</p>
                  <p className="text-xs text-muted-foreground">
                    {(selectedMapLocationJobsQuery.data?.count ?? 0)} job(s) found at this location.
                  </p>
                </div>
                {(selectedMapLocationJobsQuery.data?.results ?? []).length === 0 ? (
                  <div className="rounded-xl border border-border/70 p-4 text-sm text-muted-foreground">
                    No jobs found for this location.
                  </div>
                ) : (
                  <div className="max-h-[520px] space-y-2 overflow-y-auto pr-1">
                    {(selectedMapLocationJobsQuery.data?.results ?? []).map((job) => (
                      <div key={`location-job-${job.id}`} className="rounded-xl border border-border/70 bg-background p-3">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-foreground">{job.title || `Job ${job.id}`}</p>
                            <p className="truncate text-xs text-muted-foreground">{job.job_ref || `#${job.id}`}</p>
                          </div>
                          <Badge variant="outline" className="shrink-0">{getJobStatusLabel(job.workflow_status_code ?? job.status)}</Badge>
                        </div>
                        <p className="mt-1 text-xs text-muted-foreground">{formatSelectedJobSchedule(job) || 'Schedule not set'}</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          <Button size="sm" variant="outline" asChild>
                            <Link to={withServiceInstanceId(`/job/${job.id}`, serviceInstanceId)}>Open job</Link>
                          </Button>
                          <Button size="sm" variant="ghost" asChild>
                            <Link to={withServiceInstanceId(`/job/locations?job_id=${encodeURIComponent(String(job.id))}`, serviceInstanceId)}>View on map</Link>
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
        ) : (
        <Card className="rounded-[28px] border-border/80">
          <CardHeader className="px-4 pb-2 pt-4">
            <Tabs value={workerStatusFilter} onValueChange={(value) => setWorkerStatusFilter(value as 'all' | 'online' | 'offline')}>
              <div className="flex items-center justify-between gap-3">
                <CardTitle className="text-lg">{selectedAssignmentId ? 'Assignment Workers' : 'Workers'}</CardTitle>
                <div className="flex items-center gap-2">
                  {canAccessHotline ? (
                  <label className="flex items-center gap-2 rounded-md border border-border/70 px-2 py-1 text-xs text-muted-foreground">
                    <Checkbox
                      checked={hotlineOnly}
                      onCheckedChange={(checked) => setHotlineOnly(Boolean(checked))}
                    />
                    Hotline only
                  </label>
                  ) : null}
                  <TabsList className="grid h-8 w-auto grid-cols-3">
                    <TabsTrigger value="all" className="h-7 px-3 text-xs">All</TabsTrigger>
                    <TabsTrigger value="online" className="h-7 px-3 text-xs">Online</TabsTrigger>
                    <TabsTrigger value="offline" className="h-7 px-3 text-xs">Offline</TabsTrigger>
                  </TabsList>
                </div>
              </div>
            </Tabs>
            {selectedAssignmentId ? (
              <div className="space-y-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                <p className="font-semibold">Filtered to one assignment role</p>
                <p className="text-blue-700/80">
                  Showing workers eligible for {selectedAssignmentSkillLabel || 'this role'}.
                </p>
              </div>
            ) : null}

          </CardHeader>
          <CardContent className="space-y-3 px-4 pt-0">
            {!isLiveMode && selectedJobId && selectedJobQuery.data ? (
              <JobTeamAssignmentControls
                job={selectedJobQuery.data}
                serviceInstanceId={serviceInstanceId}
                onApplied={refreshJobAssignments}
              />
            ) : null}
            {workersLastLocationQuery.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="rounded-xl border border-border/70 p-3">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="mt-2 h-3 w-48" />
                  </div>
                ))}
              </div>
            ) : workersLastLocationQuery.isError ? (
              <p className="text-sm text-destructive">{getErrorMessage(workersLastLocationQuery.error)}</p>
            ) : displayedWorkers.length === 0 ? (
              <div className="space-y-3 rounded-xl border border-border/70 p-3 text-sm text-muted-foreground">
                <p className="font-medium text-foreground">
                  {selectedAssignmentId
                    ? `No workers found for ${selectedAssignmentSkillLabel || 'this role'}`
                    : 'No workers available to assign'}
                </p>
                {selectedAssignmentId ? (
                  <p>Try clearing the role filter, or create the assignment manually and pick a worker skill.</p>
                ) : (
                  <>
                    <p className="flex flex-wrap items-center gap-x-1 gap-y-1">
                      <span>Workers with skills should match this job&apos;s category. Workers without skills can still be assigned{selectedJobQuery.data?.category_name ? '' : '.'}</span>
                      {selectedJobQuery.data?.category_name ? (
                        <>
                          <JobCategoryBadge job={selectedJobQuery.data} />
                          <span>.</span>
                        </>
                      ) : null}
                    </p>
                    {workersWithoutEligibleSkillCount > 0 ? (
                      <p>{workersWithoutEligibleSkillCount} worker{workersWithoutEligibleSkillCount === 1 ? '' : 's'} in this branch did not match that category.</p>
                    ) : null}
                    <p>You do not need an existing open slot — a new assignment can be created directly.</p>
                  </>
                )}
                {manualAssignmentCreatePath ? (
                  <Button variant="outline" size="sm" className="rounded-lg bg-background" asChild>
                    <Link to={manualAssignmentCreatePath}>
                      Create assignment manually
                    </Link>
                  </Button>
                ) : null}
              </div>
            ) : (
              <div className="max-h-[520px] space-y-1.5 overflow-y-auto pr-1">
                {displayedWorkers.map((worker) => {
                  const isOnline = isWorkerOnline(worker)
                  const hotlineStatus = String(worker.hotline_current_status ?? '').trim().toLowerCase()
                  const isHotlineWorker = Boolean(hotlineStatus)
                  const workerKey = String(worker.id ?? getWorkerIdentifier(worker))
                  const workerBadge = getWorkerMapBadge(worker)
                  const skillLabels = getWorkerSkillLabels(worker)
                  const isSelectedOnMap = selectedWorkerMapId === workerKey
                  const assignment = getWorkerAssignment(worker)
                  const isBusy = Boolean(assignment)
                  const distanceLabel = getWorkerDistanceLabel(worker)
                  const locationText = getWorkerPreferredLocationText(worker)
                  const canRemove = canRemoveAssignmentFromJob(assignment)

                  return (
                    <div
                      key={`worker-panel-${workerKey}`}
                      role="button"
                      tabIndex={0}
                      className={`w-full rounded-xl border p-2 text-left shadow-sm transition disabled:cursor-not-allowed disabled:opacity-70 ${
                        isSelectedOnMap
                          ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100'
                          : isOnline
                            ? 'border-border/70 bg-background hover:border-emerald-300 hover:bg-emerald-50/50'
                            : 'border-slate-200 bg-slate-50 text-slate-600 hover:border-slate-300 hover:bg-slate-100'
                      }`}
                      onClick={() => handleWorkerMapSelect(worker)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          handleWorkerMapSelect(worker)
                        }
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${workerBadge?.bgClass || 'bg-slate-500'}`}>
                          {workerBadge?.number ?? '-'}
                        </span>
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className={`text-xs ${isOnline ? '' : 'bg-slate-200 text-slate-600'}`}>
                            {getWorkerInitials(worker.display_name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex min-w-0 items-center gap-1.5">
                            <Link
                              to={withServiceInstanceId(`/workers/${worker.id}`, serviceInstanceId)}
                              className={`min-w-0 flex-1 truncate text-sm font-semibold transition-colors hover:text-primary ${isOnline ? 'text-foreground' : 'text-slate-600'}`}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {worker.display_name || `Worker ${worker.id}`}
                              <span className={`ml-1.5 font-normal ${isOnline ? 'text-muted-foreground' : 'text-slate-500'}`}>
                                {getWorkerIdentifier(worker)}
                              </span>
                            </Link>
                            {skillLabels.length > 0 ? (
                              <div className="flex min-w-0 flex-wrap items-center gap-1">
                                {skillLabels.map((skillLabel) => (
                                  <Badge
                                    key={`${workerKey}-name-skill-${skillLabel}`}
                                    variant="outline"
                                    className={`max-w-[140px] truncate px-1.5 py-0 text-[10px] ${isOnline ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-100 text-slate-600'}`}
                                  >
                                    {skillLabel}
                                  </Badge>
                                ))}
                              </div>
                            ) : null}
                            <Badge className={`shrink-0 px-1.5 py-0 text-[10px] ${isOnline ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-100' : 'bg-slate-100 text-slate-600 hover:bg-slate-100'}`}>
                              {isOnline ? 'Online' : 'Offline'}
                            </Badge>
                            {canAccessHotline && isHotlineWorker ? (
                              <Badge
                                variant="outline"
                                className={hotlineStatus === 'approval'
                                  ? 'border-blue-200 bg-blue-50 px-1.5 py-0 text-[10px] text-blue-700'
                                  : hotlineStatus === 'waiting'
                                    ? 'border-amber-200 bg-amber-50 px-1.5 py-0 text-[10px] text-amber-700'
                                    : hotlineStatus === 'denied'
                                      ? 'border-rose-200 bg-rose-50 px-1.5 py-0 text-[10px] text-rose-700'
                                      : 'border-slate-200 bg-slate-100 px-1.5 py-0 text-[10px] text-slate-600'}
                              >
                                Hotline: {hotlineStatus}
                              </Badge>
                            ) : null}
                            {!isLiveMode && (selectedJobId || selectedAssignmentId) ? (
                              assignment && !selectedAssignmentId ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-6 shrink-0 rounded-md px-2 text-[11px]"
                                  disabled={!canRemove || assignmentDeleteMutation.isPending}
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    handleRemoveAssignment(assignment)
                                  }}
                                >
                                  Remove
                                </Button>
                              ) : (
                                <Button
                                  type="button"
                                  size="sm"
                                  className="h-6 shrink-0 rounded-md px-2 text-[11px]"
                                  disabled={assignmentCreateMutation.isPending || workerSkillsQuery.isLoading}
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    void handleAddWorkerToJob(worker)
                                  }}
                                >
                                  {selectedAssignmentId ? 'Assign' : 'Add'}
                                </Button>
                              )
                            ) : null}
                          </div>
                          <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-xs">
                            {distanceLabel ? (
                              <span className={isOnline ? 'text-muted-foreground' : 'text-slate-500'}>
                                {distanceLabel}
                              </span>
                            ) : (selectedJobId || selectedAssignmentId) ? (
                              <span className={isOnline ? 'text-muted-foreground' : 'text-slate-500'}>No distance</span>
                            ) : null}
                            {locationText ? (
                              <span className={`truncate ${isOnline ? 'text-muted-foreground' : 'text-slate-500'}`} title={locationText}>
                                {locationText}
                              </span>
                            ) : null}
                            <Badge variant="outline" className={`px-1.5 py-0 text-[10px] ${isBusy ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                              {isBusy ? 'Busy' : 'Available'}
                            </Badge>
                            {assignment ? (
                              <Badge variant="outline" className="border-blue-200 bg-blue-50 px-1.5 py-0 text-[10px] text-blue-700">
                                {getAssignmentStatusLabel(assignment.status)}
                              </Badge>
                            ) : null}
                            {skillLabels.length === 0 ? (
                              <span className={`text-xs ${isOnline ? 'text-muted-foreground' : 'text-slate-500'}`}>No skills</span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>
        )}
      </section>

      {!isLiveMode && !isWorkerAssignmentPage ? (
      <section>
        <Card className="rounded-[28px] border-border/80">
          <CardHeader className="gap-4 border-b border-border/70 pb-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-lg">{selectedAssignmentId ? 'Worker Assignment' : 'Location list'}</CardTitle>
                <p className="mt-1 text-sm text-muted-foreground">
                  {selectedAssignmentId
                    ? 'Select a worker with the same skill to update this assignment.'
                    : 'Select a saved location to edit or inspect it.'}
                </p>
              </div>
              <Button type="button" className="rounded-lg" onClick={startCreate}>
                <Plus className="mr-2 h-4 w-4" />
                {selectedAssignmentId ? 'New location' : 'Add'}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 p-4 sm:p-6">
            {isEditMode ? renderLocationForm() : null}
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="rounded-xl border border-border/70 p-4">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="mt-3 h-4 w-full" />
                  </div>
                ))}
              </div>
            ) : locations.length === 0 ? (
              <Empty>
                <div className="text-center">
                  <p className="text-lg font-medium">No locations found</p>
                  <p className="text-sm text-muted-foreground">Click Add to add a location for this branch.</p>
                </div>
              </Empty>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-border/70 bg-background">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableHead className="pl-5">Place</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead>Jobs</TableHead>
                      <TableHead>Workers</TableHead>
                      <TableHead>Online</TableHead>
                      <TableHead className="w-12 pr-5 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {locations.map((location) => {
                      const locationColor = getLocationMarkerPaletteEntry(location.id)
                      const isMapSelectedLocation = selectedMapLocationId === String(location.id)
                      return (
                      <TableRow
                        key={location.id}
                        onClick={() => {
                          setSelectedLocation(location)
                          setIsEditMode(true)
                        }}
                        className={cn('cursor-pointer', isMapSelectedLocation && 'ring-1 ring-inset')}
                        style={isMapSelectedLocation
                          ? {
                              backgroundColor: locationColor.softColorHex,
                              boxShadow: `inset 4px 0 0 ${locationColor.colorHex}`,
                            }
                          : undefined}
                      >
                        <TableCell className="pl-5 font-medium text-foreground">
                          <div className="flex items-center gap-2">
                            <span
                              className="h-3 w-3 shrink-0 rounded-full border"
                              style={{
                                backgroundColor: locationColor.colorHex,
                                borderColor: locationColor.borderColorHex,
                              }}
                              aria-hidden="true"
                            />
                            <span style={isMapSelectedLocation ? { color: locationColor.darkColorHex } : undefined}>{location.place}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm" onClick={(event) => event.stopPropagation()}>
                          {location.client_user && location.client_user_info?.name ? (
                            <Button variant="link" className="h-auto p-0 font-medium" asChild>
                              <Link to={(() => {
                                const match = customers.find((customer) => (
                                  String(customer.id) === String(location.client_user)
                                  || String(customer.user_id ?? '') === String(location.client_user)
                                ))
                                const params = new URLSearchParams()
                                if (serviceInstanceId) params.set('service_instance_id', String(serviceInstanceId))
                                if (match?.user_group != null) params.set('user_group_id', String(match.user_group))
                                const search = params.toString()
                                return `/job/customers/${match?.id ?? location.client_user}${search ? `?${search}` : ''}`
                              })()}>
                                {location.client_user_info.name}
                              </Link>
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">Own site</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {location.attendance_device_name
                            ? `${location.attendance_device_name}${location.attendance_device_sn ? ` (${location.attendance_device_sn})` : ''}`
                            : '-'}
                        </TableCell>
                        <TableCell
                          className="text-sm"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {(location.linked_job_count ?? 0) > 0 ? (
                            <Button variant="link" className="h-auto p-0 font-medium" asChild>
                              <Link to={buildLocationJobsLink(location.id)}>
                                {location.linked_job_count}
                              </Link>
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell
                          className="text-sm"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {(location.assigned_worker_count ?? 0) > 0 ? (
                            <Button variant="link" className="h-auto p-0 font-medium" asChild>
                              <Link to={buildLocationWorkersLink(location.id)}>
                                {location.assigned_worker_count}
                              </Link>
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell
                          className="text-sm"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {(location.online_worker_count ?? 0) > 0 ? (
                            <Button variant="link" className="h-auto p-0 font-medium text-emerald-700" asChild>
                              <Link to={buildLocationWorkersLink(location.id, true)}>
                                {location.online_worker_count}
                              </Link>
                            </Button>
                          ) : (
                            <span className="text-muted-foreground">0</span>
                          )}
                        </TableCell>
                        <TableCell className="pr-5 text-right" onClick={(event) => event.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Actions for ${location.place}`}
                                className="h-8 w-8"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Actions</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedLocation(location)
                                  setIsEditMode(true)
                                }}
                              >
                                <Pencil className="mr-2 h-4 w-4" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedLocation(location)
                                  setIsEditMode(true)
                                  setIsDrawing((location.coordinates?.length ?? 0) < 3)
                                }}
                              >
                                <LandPlot className="mr-2 h-4 w-4" />
                                {location.coordinates?.length
                                  ? `Geofence (${location.coordinates.length} pts)`
                                  : 'Add geofence'}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                disabled={deleteMutation.isPending || Boolean(location.linked_job_count)}
                                onClick={() => {
                                  if (location.linked_job_count) {
                                    return
                                  }
                                  setSelectedLocation(location)
                                  deleteMutation.mutate(String(location.id))
                                }}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </section>
      ) : null}

      <Dialog open={Boolean(skillPickerState)} onOpenChange={(open) => !open && setSkillPickerState(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Choose worker skill</DialogTitle>
            <DialogDescription>
              {skillPickerState?.worker.display_name || 'This worker'} has multiple eligible skills. Pick one to assign to this job.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {skillPickerState?.skills.map((skill) => (
              <button
                key={skill.id}
                type="button"
                className="w-full rounded-xl border border-border/70 p-3 text-left transition hover:bg-muted/50"
                onClick={() => {
                  const state = skillPickerState
                  setSkillPickerState(null)
                  if (state) {
                    void resolvePayScaleAndAssign(state.worker, skill)
                  }
                }}
              >
                <p className="text-sm font-medium text-foreground">
                  {skill.skill_role_name || skill.skill_role_title || `Skill ${skill.id}`}
                </p>
                <p className="text-xs text-muted-foreground">
                  {[skill.job_category_name, skill.pay_type_label || skill.pay_type].filter(Boolean).join(' - ') || 'No category'}
                </p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(payScalePickerState)} onOpenChange={(open) => !open && setPayScalePickerState(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Choose pay scale</DialogTitle>
            <DialogDescription>
              Select the pay scale to use for this job assignment.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {payScalePickerState?.payScales.map((payScale) => (
              <button
                key={payScale.id}
                type="button"
                className="w-full rounded-xl border border-border/70 p-3 text-left transition hover:bg-muted/50"
                onClick={() => {
                  const state = payScalePickerState
                  if (state) {
                    createAssignmentForSkill(state.skill, payScale)
                  }
                }}
              >
                <p className="text-sm font-medium text-foreground">{formatPayScaleLabel(payScale)}</p>
                <p className="text-xs text-muted-foreground">
                  {[payScale.skill_role_name, payScale.is_active === false ? 'Inactive' : 'Active'].filter(Boolean).join(' - ')}
                </p>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(assignmentClashState)} onOpenChange={(open) => !open && setAssignmentClashState(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Worker schedule clash</DialogTitle>
            <DialogDescription>
              This worker has another assignment during this job time. Review the clash details before continuing.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            {assignmentClashState?.message}
          </div>
          <p className="text-sm text-muted-foreground">
            Still want to proceed and assign this worker to the job?
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAssignmentClashState(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={assignmentCreateMutation.isPending}
              onClick={() => {
                const state = assignmentClashState
                if (!state) {
                  return
                }
                assignmentCreateMutation.mutate({
                  ...state.payload,
                  allow_schedule_clash: true,
                })
              }}
            >
              Still proceed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
